// Public intake form for merchants and techs.
//
// Mobile-first. Loaded at /intake/:token — no auth required; the token
// in the URL is the entire access-control surface. Per-field debounced
// auto-save (~1s) hits PATCH /api/intake/{token}; submit hits POST
// /api/intake/{token}/complete.
//
// All sections render visible by default — recipient should see the
// whole shape of what's being asked, not surprise-collapsibles. The
// operator-side NewApplication uses a denser layout with collapsibles
// because operators are repeat users; this surface is for one-time
// recipients, so simplicity wins.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import {
  AlertCircle,
  X,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Lock,
} from "lucide-react";
import {
  IntakeApiError,
  completeIntake,
  loadIntake,
  saveIntakeProgress,
  uploadIntakeDocument,
  type IntakeLoadResponse,
  type IntakeUploadKind,
} from "../lib/intakeApi";
import {
  applyFieldFormat,
  businessStartDateRules,
  FIELD_FORMAT_DEFAULTS,
  formatSSN,
  ownerDobRules,
  type FieldFormat,
} from "@/lib/formatters";
import {
  ACCOUNT_TYPE_OPTIONS,
  normalizeAccountType,
  OWNER_TITLE_OPTIONS,
  OWNERSHIP_TYPE_OPTIONS,
  US_STATE_OPTIONS,
} from "@/lib/labels";
import { searchMcc, MCC_BY_CODE, formatMccLabel } from "@/lib/mcc";
import { storeTypes } from "@/lib/storeTypes";
import {
  POS_PLATFORM_OPTIONS,
  POS_PLATFORM_OTHER,
  isOtherPosPlatform,
} from "@/lib/posPlatforms";
import { SearchableSelect } from "@/components/SearchableSelect";
import { SelectField } from "@/components/SelectField";
import { DatePicker } from "@/components/DatePicker";
import { SensitiveField } from "@/components/SensitiveField";
import { PercentageTotalIndicator } from "@/components/PercentageTotalIndicator";
import {
  FieldErrorText,
  SameAsToggle,
  Section,
} from "@/components/application";
import {
  selectPricingTier,
  formatFee,
  formatFnsNumber,
  formatPercent,
  effectiveAnnualSecurityBundleFee,
  CLOVER_FEE_OVERRIDE_KEYS,
  CLOVER_SECURITY_AND_TRANSARMOR_FIELDS,
  EBT_KEYS,
  FEE_OVERRIDE_FIELDS,
  FLEET_OVERRIDE_FIELDS,
  PIN_DEBIT_KEYS,
  VOYAGER_KEYS,
  WEX_KEYS,
  type FeeOverrideField,
  type PciProgram,
} from "@/lib/pricingTiers";

import { GAS_PUMP_TYPES } from "@/lib/pdiOptions";
import { mnspShippingToPdiLabel } from "@/lib/mnspPricing";
import { isRecipientActionComplete } from "@/lib/intakeStatus";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  DocumentUploadRow,
  EMPTY_MNSP_FORM,
  MnspEnrollmentSection,
  findMissingAcceptanceLabels,
  mnspServiceTypeErrors,
  fromServerMnsp,
  toWireMnsp,
  type MnspEnrollmentFormState,
} from "@/components/MnspEnrollmentSection";
import {
  getIntakeSubmitRemaining,
  validateIntakeSubmit,
} from "@/lib/applicationValidation";
import { useFieldErrors } from "@/lib/useFieldErrors";
import { intakeFieldPath } from "@/lib/intakeLocks";

// Merchant fields the operator pre-filled (merchantData paths, from the
// intake load). TF / Field read it to render those inputs read-only; the
// intake API strips the same paths from every save, so this is presentation,
// not the guard.
const IntakeLocksContext = createContext<ReadonlySet<string>>(new Set());

function useIntakeLock(fieldId: string): boolean {
  return useContext(IntakeLocksContext).has(intakeFieldPath(fieldId));
}

const LOCKED_HINT = "Set by Payment Products. Contact your representative to change it.";

function LockMark() {
  return (
    <Lock
      className="ml-1.5 inline h-3.5 w-3.5 align-[-2px] text-brand-muted"
      aria-label="Locked"
    />
  );
}

// Every control on this form carries an "ife-" prefixed element id so the
// public intake page can't collide with anything else on the document;
// validator keys are unprefixed.
const INTAKE_ID_PREFIX = "ife-";

function fieldForIntakeId(elementId: string): string {
  return elementId.startsWith(INTAKE_ID_PREFIX)
    ? elementId.slice(INTAKE_ID_PREFIX.length)
    : elementId;
}

const EMPTY_FIELD_ERRORS: FieldError[] = [];
import { pdiOrderWanted } from "@/lib/types";
import type {
  CloverSecurityAndTransarmorInput,
  FeeOverridesInput,
  FleetOverridesInput,
  PdiPrefsInput,
} from "@/lib/types";

const PDI_DEFAULTS: PdiPrefsInput = {
  posPlatform: "",
  singleOrMultiSite: "Single",
  gasPumpYn: "No",
  gasPumpType: "",
  broadbandYn: "No",
  managedWifiInterest: "",
  residentialLocation: "No",
  shippingSpeed: "Standard",
  notes: "",
};

const AUTO_SAVE_DEBOUNCE_MS = 1000;

type SaveState = "idle" | "saving" | "saved" | "error";

interface FieldError {
  /** dotted field path matching the input id, e.g. "owner.ssn" */
  field: string;
  message: string;
}

// Mirrors MerchantDataInput's shape on the wire. We keep the form
// state as a deep object so it's a 1:1 PATCH payload without per-field
// path-mapping. Empty strings (not undefined) so React inputs stay
// controlled across renders.
interface MerchantForm {
  businessName: string;
  dbaName: string;
  taxFilingName: string;
  taxFilingMethod: string;
  businessStartDate: string;
  taxID: string | null;
  mcc: string;
  storeType: string;
  businessDescription: string;
  website: string;
  contactEmail: string;
  contactPhone: string;
  businessAddress: AddressForm;
  mailingAddress: AddressForm;
  volumeDetails: VolumeDetailsForm;
  deliveryPercentages: DeliveryPercentagesForm;
  owner: OwnerForm;
  bankDetail: BankForm;
  modeOfTransaction: ModeForm;
}

interface AddressForm {
  address1: string;
  address2: string;
  city: string;
  stateCd: string;
  zip: string;
  countryCd: string;
}

interface OwnerForm {
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
  ssn: string | null;
  dateOfBirth: string;
  ownershipPct: string;
  ownershipTypeCd: string;
  driversLicenseNumber: string | null;
  driversLicenseStateCd: string;
  address: AddressForm;
}

interface BankAccountForm {
  accountNumber: string | null;
  // Double-entry confirmation of accountNumber (issue #195). UI-only: toWire
  // never sends it; the shared validator blocks submit until it matches.
  confirmAccountNumber: string | null;
  routingNumber: string | null;
  accountType: string;
  bankName: string;
}

interface BankForm {
  deposit: BankAccountForm;
  withdrawal: BankAccountForm;
}

interface ModeForm {
  eCommercePct: string;
  keyedPct: string;
  mailOrderPct: string;
  swipedPct: string;
}

interface VolumeDetailsForm {
  averageMonthlyVolume: string;
  highTicketAmount: string;
  averageTicketAmount: string;
}

type DeliveryPercentagesForm = {
  dlvrySameDayPct: string;
  dlvry0To7DaysPct: string;
  dlvry8To14DaysPct: string;
  dlvry15To30DaysPct: string;
  dlvryOver30DaysPct: string;
};

const EMPTY_ADDRESS: AddressForm = {
  address1: "",
  address2: "",
  city: "",
  stateCd: "",
  zip: "",
  countryCd: "US",
};
const EMPTY_BANK_ACCOUNT: BankAccountForm = {
  accountNumber: "",
  confirmAccountNumber: "",
  routingNumber: "",
  accountType: "BIZ",
  bankName: "",
};
const EMPTY_FORM: MerchantForm = {
  businessName: "",
  dbaName: "",
  taxFilingName: "",
  taxFilingMethod: "EIN",
  taxID: "",
  businessStartDate: "",
  mcc: "",
  storeType: "",
  businessDescription: "",
  website: "",
  contactEmail: "",
  contactPhone: "",
  businessAddress: { ...EMPTY_ADDRESS },
  mailingAddress: { ...EMPTY_ADDRESS },
  owner: {
    firstName: "",
    lastName: "",
    title: "OWNER",
    email: "",
    phone: "",
    ssn: "",
    ownershipTypeCd: "PUBCORP",
    dateOfBirth: "",
    ownershipPct: "100",
    driversLicenseNumber: "",
    driversLicenseStateCd: "",
    address: { ...EMPTY_ADDRESS },
  },
  bankDetail: {
    deposit: { ...EMPTY_BANK_ACCOUNT },
    withdrawal: { ...EMPTY_BANK_ACCOUNT },
  },
  modeOfTransaction: {
    eCommercePct: "0",
    keyedPct: "0",
    mailOrderPct: "0",
    swipedPct: "100",
  },
  deliveryPercentages: {
    dlvrySameDayPct: "100",
    dlvry0To7DaysPct: "0",
    dlvry8To14DaysPct: "0",
    dlvry15To30DaysPct: "0",
    dlvryOver30DaysPct: "0",
  },
  volumeDetails: {
    averageMonthlyVolume: "0",
    highTicketAmount: "0",
    averageTicketAmount: "0",
  },
};

// Local storage key for the welcome-back banner dismissal — keyed by
// token so different intake links each get their own banner.
function bannerDismissKey(token: string): string {
  return `intake.welcomeBack.dismissed.${token}`;
}

// isRecipientActionComplete + POST_SUBMIT_STATUSES now live in
// "@/lib/intakeStatus" so the success-view decision is unit-testable
// (Suite 5.6). Imported above.

type BankSide = "deposit" | "withdrawal";
type BankNumberField = "accountNumber" | "routingNumber";

// Single source for the censored-field key of a bank number. The
// prefix->field mapping used to be re-encoded at each site (findCensored
// Fields AND the BankAccountFields render), and that duplication is what
// let a mask bug slip in. Everything now derives the key from here.
function bankCensorKey(side: BankSide, field: BankNumberField): string {
  const suffix = field === "accountNumber" ? "AccountNumber" : "RoutingNumber";
  return `${side}${suffix}`;
}

function findCensoredFields(form: MerchantForm): string[] {
  const censored: string[] = [];
  if (form.taxID?.startsWith("****")) censored.push("taxID");
  if (form.owner?.ssn?.startsWith("****")) censored.push("ssn");
  if (form.owner?.driversLicenseNumber?.startsWith("****"))
    censored.push("driversLicenseNumber");
  for (const side of ["deposit", "withdrawal"] as const) {
    const acct = form.bankDetail?.[side]?.accountNumber;
    const rout = form.bankDetail?.[side]?.routingNumber;
    if (acct?.startsWith("****"))
      censored.push(bankCensorKey(side, "accountNumber"));
    if (rout?.startsWith("****"))
      censored.push(bankCensorKey(side, "routingNumber"));
  }
  return censored;
}

// Merge server-side load data into the form-state shape. Anything the
// server doesn't have stays at the EMPTY_FORM default; partial sections
// are merged shallowly.
function fromServer(merchant: Record<string, unknown>): MerchantForm {
  function pickAddr(src: unknown): AddressForm {
    const s = (src || {}) as Record<string, unknown>;
    return {
      address1: String(s.address1 || ""),
      address2: String(s.address2 || ""),
      city: String(s.city || ""),
      stateCd: String(s.stateCd || ""),
      zip: String(s.zip || ""),
      countryCd: String(s.countryCd || "US"),
    };
  }
  function pickAcct(src: unknown): BankAccountForm {
    const s = (src || {}) as Record<string, unknown>;
    return {
      // Server returns last-4 in `accountLast4`/`routingLast4` for
      // already-saved sensitive fields. Show last-4 prefixed by ****
      // so the recipient sees what's there without us echoing raw.
      accountNumber: s.accountLast4 ? `****${s.accountLast4}` : "",
      // Mirror the censored account into the confirm field so a reloaded,
      // untouched last-4 doesn't read as a mismatch. The validator skips the
      // match while the value is a **** placeholder; once the recipient types
      // a real account number, they re-key the confirm too.
      confirmAccountNumber: s.accountLast4 ? `****${s.accountLast4}` : "",
      routingNumber: s.routingLast4 ? `****${s.routingLast4}` : "",
      accountType: normalizeAccountType(String(s.accountType || "BIZ")),
      bankName: String(s.bankName || ""),
    };
  }
  const owner = (merchant.owner || {}) as Record<string, unknown>;
  const bank = (merchant.bankDetail || {}) as Record<string, unknown>;
  const mode = (merchant.modeOfTransaction || {}) as Record<string, unknown>;
  const volumeDetails = (merchant.volumeDetails || {}) as Record<
    string,
    unknown
  >;
  const deliveryPercentages = (merchant.deliveryPercentages || {}) as Record<
    string,
    unknown
  >;
  return {
    businessName: String(merchant.businessName || ""),
    dbaName: String(merchant.dbaName || ""),
    taxFilingName: String(merchant.taxFilingName || ""),
    taxFilingMethod: String(merchant.taxFilingMethod || "EIN"),
    // Server stores last-4 only. Display as ****1234.
    taxID: merchant.taxIdLast4 ? `****${merchant.taxIdLast4}` : "",
    mcc: String(merchant.mcc || ""),
    storeType: String(merchant.storeType || ""),
    businessDescription: String(merchant.businessDescription || ""),
    businessStartDate: String(merchant.businessStartDate || ""),
    website: String(merchant.website || ""),
    contactEmail: String(merchant.contactEmail || ""),
    contactPhone: String(merchant.contactPhone || ""),
    businessAddress: pickAddr(merchant.businessAddress),
    mailingAddress: pickAddr(merchant.mailingAddress),
    owner: {
      firstName: String(owner.firstName || ""),
      lastName: String(owner.lastName || ""),
      title: String(owner.title || "OWNER"),
      email: String(owner.email || ""),
      phone: String(owner.phone || ""),
      ssn: owner.ssnLast4 ? `****${owner.ssnLast4}` : "",
      ownershipTypeCd: String(owner.ownershipTypeCd || "PUBCORP"),
      dateOfBirth: String(owner.dateOfBirth || ""),
      ownershipPct: String(owner.ownershipPct || "100"),
      // Last-4-only hydration, matching ssn/taxID/bank. The redacted
      // storage path only ever returns driversLicenseNumberLast4; never
      // hydrate a raw DL into client state (the old String(...) fallback
      // could, breaking the masking contract this field relies on).
      driversLicenseNumber: owner.driversLicenseNumberLast4
        ? `****${owner.driversLicenseNumberLast4}`
        : "",
      driversLicenseStateCd: String(owner.driversLicenseStateCd || ""),
      address: pickAddr(owner.address),
    },
    bankDetail: {
      deposit: pickAcct(bank.deposit),
      withdrawal: pickAcct(bank.withdrawal),
    },
    deliveryPercentages: {
      dlvrySameDayPct: String(deliveryPercentages.dlvrySameDayPct || 100),
      dlvry0To7DaysPct: String(deliveryPercentages.dlvry0To7DaysPct || 0),
      dlvry8To14DaysPct: String(deliveryPercentages.dlvry8To14DaysPct || 0),
      dlvry15To30DaysPct: String(deliveryPercentages.dlvry15To30DaysPct || 0),
      dlvryOver30DaysPct: String(deliveryPercentages.dlvryOver30DaysPct || 0),
    },
    modeOfTransaction: {
      eCommercePct: String(mode.eCommercePct || 0),
      keyedPct: String(mode.keyedPct || 0),
      mailOrderPct: String(mode.mailOrderPct || 0),
      swipedPct: String(mode.swipedPct ?? 100),
    },
    volumeDetails: {
      averageMonthlyVolume: String(volumeDetails.averageMonthlyVolume || 0),
      highTicketAmount: String(volumeDetails.highTicketAmount || 0),
      averageTicketAmount: String(volumeDetails.averageTicketAmount || 0),
    },
  };
}

// Map form state back to the wire shape, stripping any **** masked
// values (so we don't re-send a placeholder as if it were a real edit).
function toWire(form: MerchantForm): Record<string, unknown> {
  function unmask(s: string | null): string {
    if (!s) return "";
    return s.startsWith("****") ? "" : s;
  }
  // For digits-only sensitive fields (bank account/routing), unmask
  // BEFORE stripping non-digits. Stripping first would remove the ****
  // mask prefix, so a masked "****1234" would survive as "1234" and be
  // re-sent as a real value, corrupting the stored account/routing
  // number for any returning recipient who never re-typed the field.
  function unmaskDigits(s: string | null | undefined): string {
    return unmask((s ?? "").trim()).replace(/\D/g, "");
  }
  function addrOut(a: AddressForm): Record<string, unknown> {
    return { ...a };
  }

  return {
    businessName: form.businessName.trim(),
    dbaName: form.dbaName.trim(),
    taxFilingName: form.taxFilingName.trim(),
    taxFilingMethod: form.taxFilingMethod,
    taxID: form.taxID ? unmask(form.taxID?.trim()) : "",
    mcc: form.mcc.trim(),
    storeType: form.storeType.trim(),
    businessDescription: form.businessDescription.trim(),
    website: form.website.trim(),
    businessStartDate: String(form.businessStartDate || ""),
    contactEmail: form.contactEmail.trim().toLowerCase(),
    contactPhone: form.contactPhone.trim(),
    businessAddress: addrOut(form.businessAddress),
    mailingAddress: addrOut(form.mailingAddress),
    owner: {
      firstName: form.owner.firstName.trim(),
      lastName: form.owner.lastName.trim(),
      title: form.owner.title,
      email: form.owner.email.trim().toLowerCase(),
      phone: form.owner.phone.trim(),
      ssn: form.owner.ssn ? unmask(form.owner.ssn.trim()) : "",
      dateOfBirth: form.owner.dateOfBirth.trim(),
      ownerMobilePhone: form.owner.phone.trim(),
      ownershipTypeCd: form.owner.ownershipTypeCd,
      ownershipPct: parseInt(form.owner.ownershipPct, 10) || 0,
      driversLicenseNumber: form.owner.driversLicenseNumber
        ? unmask(form.owner.driversLicenseNumber.trim())
        : "",
      driversLicenseStateCd: form.owner.driversLicenseStateCd
        .trim()
        .toUpperCase(),
      address: addrOut(form.owner.address),
    },
    bankDetail: {
      deposit: {
        accountNumber: unmaskDigits(form.bankDetail.deposit.accountNumber),
        routingNumber: unmaskDigits(form.bankDetail.deposit.routingNumber),
        accountType: form.bankDetail.deposit.accountType,
        bankName: form.bankDetail.deposit.bankName.trim(),
      },
      withdrawal: {
        accountNumber: unmaskDigits(form.bankDetail.withdrawal?.accountNumber),
        routingNumber: unmaskDigits(form.bankDetail.withdrawal?.routingNumber),
        accountType: form.bankDetail.withdrawal?.accountType,
        bankName: form.bankDetail.withdrawal?.bankName.trim(),
      },
    },
    volumeDetails: {
      averageMonthlyVolume: parseInt(
        form.volumeDetails.averageMonthlyVolume,
        10,
      ),
      highTicketAmount: parseInt(form.volumeDetails.highTicketAmount, 10),
      averageTicketAmount: parseInt(form.volumeDetails.averageTicketAmount, 10),
    },
    deliveryPercentages: {
      dlvrySameDayPct: parseInt(form.deliveryPercentages.dlvrySameDayPct, 10),
      dlvry0To7DaysPct: parseInt(form.deliveryPercentages.dlvry0To7DaysPct, 10),
      dlvry8To14DaysPct: parseInt(
        form.deliveryPercentages.dlvry8To14DaysPct,
        10,
      ),
      dlvry15To30DaysPct: parseInt(
        form.deliveryPercentages.dlvry15To30DaysPct,
        10,
      ),
      dlvryOver30DaysPct: parseInt(
        form.deliveryPercentages.dlvryOver30DaysPct,
        10,
      ),
    },
    modeOfTransaction: {
      eCommercePct: parseInt(form.modeOfTransaction.eCommercePct, 10) || 0,
      keyedPct: parseInt(form.modeOfTransaction.keyedPct, 10) || 0,
      mailOrderPct: parseInt(form.modeOfTransaction.mailOrderPct, 10) || 0,
      swipedPct: parseInt(form.modeOfTransaction.swipedPct, 10) || 0,
    },
  };
}

export function IntakeForm() {
  const { token = "" } = useParams<{ token: string }>();
  // Required-document upload state (issue #63). Server-truth on load
  // (hasDriversLicenseImage / hasVoidedCheckImage), flipped true locally
  // only after a PUT fully succeeds.
  const [docUploads, setDocUploads] = useState({
    driversLicense: false,
    voidedCheck: false,
  });
  // Presigned GET URLs for whatever's already on file, so the upload rows
  // can show a thumbnail after a page refresh (a file picked in-session is
  // previewed from the File object inside DocumentUploadRow itself).
  const [docPreviewUrls, setDocPreviewUrls] = useState<{
    driversLicense: string | null;
    voidedCheck: string | null;
  }>({ driversLicense: null, voidedCheck: null });
  // Flipped true when a submit attempt finds a required document missing,
  // so the upload rows highlight (Susan, dev 2026-07-14). Cleared once the
  // merchant uploads (each row only highlights while still not uploaded).
  const [showUploadErrors, setShowUploadErrors] = useState(false);
  async function handleUploadDocument(kind: IntakeUploadKind, file: File) {
    await uploadIntakeDocument(token, kind, file);
    setDocUploads((prev) => ({
      ...prev,
      [kind === "DRIVERS_LICENSE" ? "driversLicense" : "voidedCheck"]: true,
    }));
  }
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [meta, setMeta] = useState<IntakeLoadResponse | null>(null);
  const lockedFields = useMemo<ReadonlySet<string>>(
    () => new Set(meta?.lockedFields ?? []),
    [meta],
  );
  const mailingLocked = [...lockedFields].some((p) =>
    p.startsWith("mailingAddress."),
  );
  const withdrawalLocked = [...lockedFields].some((p) =>
    p.startsWith("bankDetail.withdrawal."),
  );
  const [form, setForm] = useState<MerchantForm>(EMPTY_FORM);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // True when the recipient's submit was auto-handed off to the
  // CardConnect boarding queue. Drives a different success copy: the
  // signature email is on its way; no operator review step required.
  const [autoSubmitted, setAutoSubmitted] = useState(false);
  // CardConnect signature URL once the SQS handler returns it. While
  // null on the post-submit success page, we poll the GET endpoint
  // every few seconds; when it lands, we surface a "Sign now" button
  // so the merchant can sign in-browser without waiting for the email.
  // Stays null on the operator-submit path (URL only appears after
  // operator reviews + submits, which happens later).
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  // True once the recipient clicks through to CardConnect to sign. Drives
  // the unmissable "you're done, you can close this window" completion
  // panel (issue #35). We can't observe the external signature completing
  // in real time, so clicking the handoff is the signal we surface the
  // done-state on.
  const [signatureLaunched, setSignatureLaunched] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [censoredFields, setCensoredFields] = useState<string[]>([]);
  // Auto-fill notice shown when submit copied biz->mailing or deposit->withdrawal
  const [autoFillNotice, setAutoFillNotice] = useState<string | null>(null);
  // Mailing-address-same-as-business + withdrawal-account-same-as-deposit
  // checkboxes — parity with NewApplication's pattern (P2-4).
  const [mailingSameAsBusiness, setMailingSameAsBusiness] = useState(false);
  const [withdrawalSameAsDeposit, setWithdrawalSameAsDeposit] = useState(true);
  const [showCountry, setShowCountry] = useState(false);
  const [welcomeBackBanner, setWelcomeBackBanner] = useState(false);
  // MNSP bundle state. The section renders when meta.mnspEnrollmentRequested
  // is true (operator dispatched with mnspBundle=true). mnspError surfaces
  // the missing-acceptance message when local validation fails on submit;
  // server-side enforcement in intakeApiHandler._validate_mnsp_acceptances
  // catches any client-side bypass.
  const [mnspForm, setMnspForm] =
    useState<MnspEnrollmentFormState>(EMPTY_MNSP_FORM);
  // Always-latest mirrors of form + mnspForm. patchForm and
  // setMnspFormAndSave each schedule a save that includes BOTH blobs, but
  // each only receives one fresh blob and closes over the other from render
  // scope. When form and MNSP change in the same tick, the closed-over copy
  // is stale and would PATCH an out-of-date blob. Reading the counterpart
  // from a ref (updated synchronously in each setter, and re-synced on every
  // render below) fixes that.
  const formRef = useRef(form);
  const mnspFormRef = useRef(mnspForm);
  useEffect(() => {
    formRef.current = form;
  }, [form]);
  useEffect(() => {
    mnspFormRef.current = mnspForm;
  }, [mnspForm]);
  const [mnspError, setMnspError] = useState<string | null>(null);
  const mnspRequested = !!meta?.mnspEnrollmentRequested;
  // Independent of mnspRequested -- true when the operator dispatched
  // with terminalRentalBundle=true. Either or both may be true.
  const terminalRentalRequested = !!meta?.terminalRentalRequested;
  // Heading/copy label reflecting whichever of MNSP enrollment / terminal
  // rental was actually requested, so a rental-only merchant isn't told
  // they're enrolling in MNSP when they aren't. Used by both the mnspOnly
  // sign-only view and the submitted success page below.
  const mnspSectionLabel =
    mnspRequested && terminalRentalRequested
      ? "MNSP enrollment & terminal rental"
      : mnspRequested
        ? "MNSP enrollment"
        : "terminal rental";
  // True when the admin filled the entire merchant form and this intake
  // token was generated solely for the merchant to sign the MNSP section.
  // All merchant-data fields are hidden; only the MNSP section renders.
  const mnspOnly = !!meta?.mnspOnly;
  // Distinct from mnspOnly: this application is never submitted to
  // CardConnect at all, in either creation mode. Doesn't change which
  // fields render (that's mnspOnly's job) -- only used to fix copy that
  // would otherwise falsely imply a CardConnect submission happened.
  const enrollmentOnly = meta?.applicationPath === "MNSP_ENROLLMENT_ONLY";

  const [pdiPrefs, setPdiPrefs] = useState<PdiPrefsInput>(PDI_DEFAULTS);
  // Always-latest mirror of pdiPrefs so the debounced autosave can fold the
  // current PDI block into an MNSP- or merchant-triggered save without a
  // stale closure. PDI shipping speed is derived from the MNSP selection at
  // save time (the PDI section no longer has its own shipping dropdown).
  const pdiPrefsRef = useRef(pdiPrefs);
  const [pdiOpen, setPdiOpen] = useState(false);
  // POS platform "Other" mode. Latches true when the recipient picks the
  // Other option (so the free-text input shows even before they type).
  // Also derived from the stored value: a hydrated posPlatform that isn't
  // a known option is, by definition, a manually entered "Other" value.
  const [posOtherSelected, setPosOtherSelected] = useState(false);
  // Pricing confirmation dialog (issue #62): opens after all client-side
  // gates pass; the actual submit runs only on confirm.
  const [showPricingConfirm, setShowPricingConfirm] = useState(false);
  // Gas pump "Other" latch (issue #66), same pattern as posOtherSelected.
  const [gasOtherSelected, setGasOtherSelected] = useState(false);
  const posOtherMode =
    posOtherSelected || isOtherPosPlatform(pdiPrefs.posPlatform);
  // Whether a PDI managed-network order is actually in play for this
  // application -- see pdiOrderWanted. sendPdiOrder is operator-only (see
  // intakeApiHandler._strip_operator_only_pdi_fields) and never editable
  // here -- read only, so the merchant is never shown a PDI setup section
  // for an order that will never be sent.
  const pdiOrderInPlay = pdiOrderWanted(pdiPrefs.sendPdiOrder, enrollmentOnly);

  function updatePdi<K extends keyof PdiPrefsInput>(
    key: K,
    value: PdiPrefsInput[K],
  ) {
    setPdiPrefs((prev) => {
      const next = { ...prev, [key]: value };
      pdiPrefsRef.current = next;
      scheduleSave(form, mnspForm, next);
      return next;
    });
  }

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorBannerRef = useRef<HTMLDivElement>(null);
  const isCensored = (field: string) => censoredFields.includes(field);

  // Live percentage totals — recomputed every render (cheap; the sum is
  // four/five Number() calls). The PercentageTotalIndicator renders
  // amber until the total is exactly 100 so users see the issue before
  // they hit Submit, avoiding the CardConnect-400 round-trip that was
  // historically the #1 cause of failed boardings.
  const modeTotal =
    (Number(form.modeOfTransaction.eCommercePct) || 0) +
    (Number(form.modeOfTransaction.keyedPct) || 0) +
    (Number(form.modeOfTransaction.mailOrderPct) || 0) +
    (Number(form.modeOfTransaction.swipedPct) || 0);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    loadIntake(token)
      .then((data) => {
        if (cancelled) return;
        setMeta(data);
        setDocUploads({
          driversLicense: !!data.hasDriversLicenseImage,
          voidedCheck: !!data.hasVoidedCheckImage,
        });
        setDocPreviewUrls({
          driversLicense: data.driversLicenseImageUrl ?? null,
          voidedCheck: data.voidedCheckImageUrl ?? null,
        });
        const servData = fromServer(data.merchantData);
        if (data?.errorDetails) {
          setSaveError(String(data.errorDetails) || "");
        }
        setForm(servData);
        setMnspForm(
          fromServerMnsp(
            (data.merchantData?.mnspEnrollment as Record<string, unknown>) ??
              null,
          ),
        );
        if (data.pdiPrefs) {
          setPdiPrefs((prev) => {
            const merged = { ...prev, ...(data.pdiPrefs as PdiPrefsInput) };
            pdiPrefsRef.current = merged;
            return merged;
          });
        }
        setCensoredFields(findCensoredFields(servData));
        setProgressPct(data.intakeProgressPct);
        // If returning recipient (any progress saved), show one-time banner.
        if (data.intakeProgressPct > 0) {
          const dismissed = (() => {
            try {
              return localStorage.getItem(bannerDismissKey(token)) === "1";
            } catch {
              return false;
            }
          })();
          if (!dismissed) setWelcomeBackBanner(true);
        }
        // "Same as business address" starts unchecked. It's ticked only
        // when a saved mailing address matches the business address
        // exactly (e.g. the operator, or this recipient earlier, entered
        // the same address for both).
        {
          const biz = servData.businessAddress;
          const mail = servData.mailingAddress;
          const mailMatches =
            !!mail.address1 &&
            mail.address1 === biz.address1 &&
            mail.city === biz.city &&
            mail.stateCd === biz.stateCd &&
            mail.zip === biz.zip;
          setMailingSameAsBusiness(mailMatches);
        }
        // Withdrawal: when the recipient is returning to a partially-filled
        // form, treat a differing account as "no, they're different". For a
        // brand-new load (no progress yet), keep the box checked so the
        // recipient sees the shorter form.
        if (data.intakeProgressPct > 0) {
          const dep = servData.bankDetail.deposit;
          const wd = servData.bankDetail.withdrawal;
          const wdDiffers =
            !!wd?.accountNumber &&
            (wd.accountNumber !== dep.accountNumber ||
              wd.routingNumber !== dep.routingNumber ||
              wd.bankName !== dep.bankName);
          setWithdrawalSameAsDeposit(!wdDiffers);
        }
        // Lock the form to the success page once the row has moved
        // past the recipient's editable window. Without this, a refresh
        // after auto-submit (which transitions through READY_FOR_REVIEW
        // -> SIGNATURE_SENT -> ... within seconds) re-rendered the
        // editable form, letting the merchant re-submit a duplicate.
        // (isRecipientActionComplete lives in @/lib/intakeStatus; the
        // submit-failure handler reuses it.)
        if (
          isRecipientActionComplete(
            data.status,
            !!data.mnspOnly,
            data.mnspEnrollmentSubmittedAt,
          )
        ) {
          setSubmitted(true);
          // Hydrate autoSubmitted from the row so the post-refresh
          // success page picks the right variant (with/without the
          // "Sign now" button). On the original /complete response
          // we set this from completeResp.autoSubmitted; after a
          // refresh, mirror it from the row's AutoSubmit flag.
          if (data.autoSubmit) {
            setAutoSubmitted(true);
            if (data.signatureUrl) {
              setSignatureUrl(data.signatureUrl);
            }
          }
        }
      })
      .catch((e: unknown) => {
        // Don't log the error to console — it may include the response
        // body which could carry partial PII echoed back. The IntakeApi
        // error envelope already carries a user-safe message we surface
        // via setLoadError.
        if (cancelled) return;
        if (e instanceof IntakeApiError) {
          setLoadError(e.message);
        } else {
          setLoadError("Could not load the application.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // We intentionally only run this on mount / token change.
  }, [token]);

  // Sign-now polling. Once the merchant has submitted with autoSubmit=true,
  // poll the GET endpoint every 4 seconds for up to 90 seconds looking
  // for the CardConnect signatureUrl to land. SQS auto-submit typically
  // completes in a few seconds; we cap the poll so we stop hammering the
  // API if something failed (silent-failure code path covered separately
  // by the ops alert). On the operator-submit path, autoSubmitted is
  // false so polling is skipped -- the URL only appears after the
  // operator clicks Submit later.
  useEffect(() => {
    if (!submitted || !autoSubmitted || !token || signatureUrl) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();
    const POLL_INTERVAL_MS = 4000;
    const POLL_TIMEOUT_MS = 90_000;

    const poll = () => {
      loadIntake(token)
        .then((data) => {
          if (cancelled) return;
          if (data.signatureUrl) {
            setSignatureUrl(data.signatureUrl);
            return;
          } else if (data?.errorDetails) {
            setSaveError(String(data.errorDetails) || "");
          }
          if (Date.now() - startedAt < POLL_TIMEOUT_MS) {
            timer = setTimeout(poll, POLL_INTERVAL_MS);
          }
        })
        .catch(() => {
          // Soft-fail: if the GET errors transiently, try once more
          // on schedule. If it keeps erroring, the timeout will end
          // the poll loop. The merchant's success page still shows
          // the "check your email" fallback copy.
          if (!cancelled && Date.now() - startedAt < POLL_TIMEOUT_MS) {
            timer = setTimeout(poll, POLL_INTERVAL_MS);
          }
        });
    };
    timer = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [submitted, autoSubmitted, token, signatureUrl]);

  // Schedule a debounced save whenever the form changes after the
  // initial load. Submitted forms don't auto-save (recipient is done).
  // Both the merchant form and (when MNSP-bundled) the MNSP form fold
  // into the same merchantData wire payload — the backend persists them
  // together under Merchant + Merchant.mnspEnrollment.
  const scheduleSave = useCallback(
    (
      nextForm: MerchantForm,
      nextMnsp: MnspEnrollmentFormState,
      nextPdi?: PdiPrefsInput,
    ) => {
      if (submitted) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setSaveState("saving");
      debounceRef.current = setTimeout(() => {
        const merchantData = toWire(nextForm);
        if (mnspRequested || terminalRentalRequested) {
          merchantData.mnspEnrollment = toWireMnsp(
            nextMnsp,
            mnspRequested,
            terminalRentalRequested,
          );
        }
        const patch: {
          merchantData: Record<string, unknown>;
          pdiPrefs?: Record<string, unknown>;
        } = { merchantData };
        // PDI shipping speed is the MNSP selection (single source of truth,
        // no PDI-side dropdown). Fold the current PDI block (nextPdi when this
        // save was PDI-triggered, else the latest mirror) into every save so
        // an MNSP shipping change propagates to the PDI order even when no PDI
        // field was touched.
        const effectivePdi = {
          ...(nextPdi ?? pdiPrefsRef.current),
          shippingSpeed: mnspShippingToPdiLabel(nextMnsp.shippingSpeed),
        };
        pdiPrefsRef.current = effectivePdi;
        patch.pdiPrefs = effectivePdi as Record<string, unknown>;
        saveIntakeProgress(token, patch)
          .then((r) => {
            setSaveState("saved");
            setProgressPct(r.intakeProgressPct);
            setSaveError(null);
            setLastSavedAt(new Date());
          })
          .catch((e: unknown) => {
            setSaveState("error");
            setSaveError(
              e instanceof IntakeApiError ? e.message : "Save failed.",
            );
          });
      }, AUTO_SAVE_DEBOUNCE_MS);
    },
    [submitted, token, mnspRequested, terminalRentalRequested],
  );

  // Patch helpers: each takes a producer and updates form + schedules save.
  function patchForm(updater: (prev: MerchantForm) => MerchantForm) {
    setForm((prev) => {
      const next = updater(prev);
      formRef.current = next;
      // Pair with the latest MNSP blob (ref), not the render-scope closure.
      scheduleSave(next, mnspFormRef.current);
      return next;
    });
  }

  // MNSP block has its own setter (the section owns its full state shape)
  // but shares the same debounced save pipeline so a single network call
  // covers both blobs on every change.
  function setMnspFormAndSave(next: MnspEnrollmentFormState) {
    mnspFormRef.current = next;
    setMnspForm(next);
    // Pair with the latest merchant form (ref), not the render-scope closure.
    scheduleSave(formRef.current, next);
    // Clear any stale missing-acceptance banner the moment the user
    // edits something — re-shown on next submit attempt if still bad.
    if (mnspError) setMnspError(null);
  }

  function setField<K extends keyof MerchantForm>(
    key: K,
    value: MerchantForm[K],
  ) {
    patchForm((p) => ({ ...p, [key]: value }));
  }

  function localValidate(): FieldError[] {
    return validateIntakeSubmit({
      form,
      mailingSameAsBusiness,
      withdrawalSameAsDeposit,
      enrollmentOnly,
      isCensored,
    });
  }

  // Recomputed every render so a message follows the field as the recipient
  // types. useFieldErrors decides what's allowed on screen yet: a field
  // speaks up once it's been blurred, Submit reveals the rest. mnspOnly rows
  // are gated entirely by the MNSP block, so they have no merchant rules.
  const clientErrors = mnspOnly ? EMPTY_FIELD_ERRORS : localValidate();
  const {
    fieldError,
    visibleErrors,
    onBlurCapture,
    beginSubmit,
    setServerErrors,
  } = useFieldErrors(clientErrors, { fieldForElementId: fieldForIntakeId });

  // Scroll the first error into view + focus its input. Used by client-
  // side validation AND when the public REST endpoint rejects a save
  // with field-attributable errors. Centralized so both paths feel
  // identical to the recipient.
  function focusFirstError(errs: readonly FieldError[]) {
    setTimeout(() => {
      const first = errs[0];
      if (!first) return;
      const el = document.getElementById(`ife-${first.field}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        (el as HTMLElement).focus?.();
      } else if (errorBannerRef.current) {
        errorBannerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 0);
  }

  function scrollToMnspSection() {
    document
      .getElementById("mnsp-enrollment-section-anchor")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  // Evaluate the MNSP block (acceptances, printed name, signature, and the
  // required DL + voided-check uploads) and surface what's missing: set the
  // inline mnspError text and flip the upload-row highlight. Returns true
  // when something is missing. Shared by handleSubmit and the "required
  // fields still need attention" hint so BOTH highlight the MNSP items --
  // the hint previously only ran localValidate() (merchant-data fields), so
  // clicking it never highlighted the upload rows (Susan, dev 2026-07-14).
  // Does not scroll; callers decide, so merchant-field errors can take
  // scroll priority over the MNSP section.
  function surfaceMnspErrors(): boolean {
    if (!(mnspRequested || terminalRentalRequested || mnspOnly)) {
      setShowUploadErrors(false);
      return false;
    }
    const missing = findMissingAcceptanceLabels(
      mnspForm,
      mnspRequested,
      terminalRentalRequested,
    );
    const missingName = !mnspForm.acceptedBy.trim();
    const missingSignature = !mnspForm.signatureImage;
    // Required documents (issue #63): DL + voided check. The upload rows
    // are always shown in the merchant intake, and the server rejects a
    // submit without them (_validate_mnsp_uploads).
    const missingDriversLicense = !docUploads.driversLicense;
    const missingVoidedCheck = !docUploads.voidedCheck;
    const missingUploads = missingDriversLicense || missingVoidedCheck;
    const serviceTypeErrors = mnspOnly
      ? []
      : mnspServiceTypeErrors(mnspForm, mnspRequested);
    // Reflect upload state into the highlight flag every call so it clears
    // once both are uploaded and re-raises if one is removed.
    setShowUploadErrors(missingUploads);
    if (
      !(
        missing.length > 0 ||
        missingName ||
        missingSignature ||
        missingUploads ||
        serviceTypeErrors.length > 0
      )
    ) {
      return false;
    }
    const parts: string[] = [];
    if (serviceTypeErrors.length > 0) {
      parts.push(...serviceTypeErrors.map((e) => e.message));
    }
    if (missing.length > 0) {
      parts.push(
        `Please acknowledge the following section${
          missing.length === 1 ? "" : "s"
        }: ${missing.join(", ")}.`,
      );
    }
    if (missingName) {
      parts.push(`Add your printed name to the ${mnspSectionLabel} section.`);
    }
    if (missingSignature) {
      parts.push(`Sign the ${mnspSectionLabel} section using the signature pad.`);
    }
    if (missingUploads) {
      const docs: string[] = [];
      if (missingDriversLicense) docs.push("driver's license");
      if (missingVoidedCheck) docs.push("voided check");
      parts.push(`Upload your ${docs.join(" and ")}.`);
    }
    setMnspError(parts.join(" "));
    return true;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting || submitted) return;
    setAutoFillNotice(null);

    // In mnspOnly mode clientErrors is empty by construction, so this only
    // gates on the MNSP acceptance block below.
    const errs = beginSubmit();
    if (errs.length > 0) {
      focusFirstError(errs);
      return;
    }

    // MNSP gate: when the section is rendered, all four acceptance
    // boxes plus the printed-name field must be filled before we even
    // call the server. Server-side enforcement in
    // intakeApiHandler._validate_mnsp_acceptances catches anything
    // that slips through.
    if (surfaceMnspErrors()) {
      scrollToMnspSection();
      return;
    }

    setMnspError(null);

    // Pricing confirmation step (issue #62, Michael 2026-07-13): show
    // "this is your pricing" once, at the end, instead of a live
    // preview the merchant could game by adjusting their volume.
    //
    // Shown for any application that has CardConnect bundled with it,
    // including the mnspOnly sign-only / re-sign pages (the merchant should
    // still confirm the processing pricing before submitting). Skipped only
    // for MNSP-only enrollment applications, which never touch CardConnect and
    // already show their MNSP/rental fees on the page itself (Susan, dev
    // 2026-09-09). Volume is hydrated into the form from the loaded merchant
    // data on every row, so it is present even when the fields are hidden.
    const tier = enrollmentOnly
      ? null
      : selectPricingTier(form.volumeDetails.averageMonthlyVolume);
    if (tier) {
      setShowPricingConfirm(true);
      return;
    }
    await performSubmit();
  }

  // The actual submit work: pre-submit autofill, final flush, /complete.
  // Reached via the pricing confirmation dialog, or directly for
  // mnspOnly rows / when no tier resolves.
  async function performSubmit() {
    if (submitting || submitted) return;

    // Fill any blank withdrawal/mailing fields from deposit/biz before
    // submitting. Compute these LOCALLY in submitForm — never mutate
    // the visible form state, so a failed submit doesn't silently
    // rewrite what the user sees. Only after a successful submit do
    // we surface a brief notice that we filled them in.
    const dep = form.bankDetail.deposit;
    const wd = form.bankDetail.withdrawal;

    const bnkDet: { deposit: BankAccountForm; withdrawal: BankAccountForm } = {
      deposit: dep,
      withdrawal: {
        accountNumber: wd?.accountNumber || dep.accountNumber,
        confirmAccountNumber:
          wd?.confirmAccountNumber || dep.confirmAccountNumber,
        routingNumber: wd?.routingNumber || dep.routingNumber,
        accountType: wd?.accountType || dep.accountType,
        bankName: wd?.bankName || dep.bankName,
      },
    };

    const bizAdd = form.businessAddress;
    const mailAdd = form.mailingAddress;
    const mailAutoFilled = mailingSameAsBusiness;
    const filledMailAddress: AddressForm = mailAutoFilled
      ? { ...bizAdd }
      : mailAdd;

    const submitForm = {
      ...form,
      bankDetail: bnkDet,
      mailingAddress: filledMailAddress,
      dbaName: form.dbaName.trim() || form.businessName,
    };

    // Always flush the latest state before completing -- never gated on a
    // pending debounce. If the recipient's last edit was a PDI field, or no
    // save happened to be in flight, the old debounce-gated flush either
    // dropped pdiPrefs (server kept stale prefs) or skipped the final
    // merchantData entirely, so /complete ran against stale data. We send
    // the full patch (merchantData + pdiPrefs) unconditionally and await it.
    // Fold the MNSP block in when this intake was dispatched with
    // mnspBundle=true so the acceptance timestamps are present before the
    // server-side gate runs.
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    try {
      const merchantData = toWire(submitForm);
      // Fold the signed MNSP/rental block into the final save for the
      // bundled intake (mnspRequested/terminalRentalRequested) AND the
      // admin-fill sign-only intake (mnspOnly). Without the mnspOnly arm
      // the merchant's signature was never sent, so the sign-only flow
      // captured nothing.
      if (mnspRequested || terminalRentalRequested || mnspOnly) {
        merchantData.mnspEnrollment = toWireMnsp(
          mnspForm,
          mnspRequested,
          terminalRentalRequested,
        );
      }
      const r = await saveIntakeProgress(token, {
        merchantData,
        pdiPrefs: pdiPrefs as unknown as Record<string, unknown>,
      });
      setProgressPct(r.intakeProgressPct);
    } catch (e: unknown) {
      // If the final save was rejected with structured field errors
      // (server-side semantic validation), surface those inline rather
      // than swallowing. The recipient just hit Submit; they expect
      // immediate feedback.
      if (e instanceof IntakeApiError && e.fieldErrors.length > 0) {
        setServerErrors(e.fieldErrors);
        focusFirstError(e.fieldErrors);
        return;
      }
      // Otherwise continue to submit. The server already has earlier
      // state and the recipient can be informed via the catch below.
    }
    setSubmitting(true);
    setSaveError(null);
    try {
      const completeResp = await completeIntake(token);
      setAutoSubmitted(!!completeResp.autoSubmitted);
      setSubmitted(true);
      if (withdrawalSameAsDeposit || mailAutoFilled) {
        const parts: string[] = [];
        if (!mnspOnly) {
          if (mailAutoFilled)
            parts.push("mailing address from your business address");
          if (withdrawalSameAsDeposit)
            parts.push("withdrawal account from your deposit account");
          setAutoFillNotice(`We copied your ${parts.join(" and ")}.`);
        }
      }
    } catch (e: unknown) {
      if (e instanceof IntakeApiError && e.fieldErrors.length > 0) {
        setServerErrors(e.fieldErrors);
        focusFirstError(e.fieldErrors);
      }
      // The /complete call failed on our end, but it may have partially
      // succeeded server-side (the row can transition before the response
      // is lost to a network blip). Re-read the status before leaving the
      // recipient on an editable form: if the row is already past submit,
      // show the success view instead of inviting a duplicate submission.
      try {
        const view = await loadIntake(token);
        if (
          isRecipientActionComplete(
            view.status,
            !!view.mnspOnly,
            view.mnspEnrollmentSubmittedAt,
          )
        ) {
          if (view.signatureUrl) {
            setAutoSubmitted(true);
            setSignatureUrl(view.signatureUrl);
          }
          setSubmitted(true);
          return;
        }
      } catch {
        // Status re-read failed too; fall through to the generic error.
      }
      setSaveError(e instanceof IntakeApiError ? e.message : "Submit failed.");
    } finally {
      setSubmitting(false);
    }
  }

  function dismissWelcomeBanner() {
    setWelcomeBackBanner(false);
    try {
      localStorage.setItem(bannerDismissKey(token), "1");
    } catch {
      // localStorage can be unavailable in private mode — banner just
      // re-appears next visit, no harm done.
    }
  }

  // ----- render -------------------------------------------------------------

  if (loading) {
    return (
      <Shell>
        <p className="text-brand-muted">Loading…</p>
      </Shell>
    );
  }
  if (loadError) {
    return (
      <Shell>
        <h1 className="mb-2 text-xl font-semibold text-brand-text">
          This link can't be opened
        </h1>
        <p className="text-brand-muted">{loadError}</p>
        <p className="mt-4 text-sm text-brand-muted">
          Contact your Payment Products representative to get a new link.
        </p>
      </Shell>
    );
  }
  if (submitted) {
    return (
      <Shell>
        <h1 className="mb-2 text-xl font-semibold text-brand-text">
          {mnspOnly || enrollmentOnly
            ? mnspRequested || terminalRentalRequested
              ? `${mnspSectionLabel[0].toUpperCase()}${mnspSectionLabel.slice(1)} submitted`
              : "Enrollment submitted"
            : "Application submitted"}
        </h1>
        {mnspOnly ? (
          <p className="text-brand-muted">
            Thank you. Your {mnspSectionLabel} has been received. Your Payment
            Products representative will be in touch if anything else is needed.
          </p>
        ) : autoSubmitted ? (
          signatureUrl ? (
            signatureLaunched ? (
              // Unmissable completion state (issue #35). Once the recipient
              // has been handed off to CardConnect to sign, tell them in
              // plain, large type that they're done and can close the
              // window. Sized for a non-technical owner-operator. A small
              // fallback link reopens the signature page if they closed the
              // tab before finishing.
              <div className="rounded-xl border border-success/40 bg-success/10 p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle2
                    className="mt-0.5 h-8 w-8 shrink-0 text-success"
                    aria-hidden="true"
                  />
                  <div>
                    <p className="text-lg font-semibold text-brand-text">
                      That's everything. You're all set.
                    </p>
                    <p className="mt-1 text-brand-muted">
                      Once you've finished signing on the CardConnect page, your
                      application is complete. You can safely close this window.
                    </p>
                    <p className="mt-3 text-sm text-brand-muted">
                      Didn't finish, or closed the signing tab by mistake?{" "}
                      <a
                        href={signatureUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-blue underline hover:text-brand-blue/80"
                      >
                        Reopen the signature page
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              // Auto-submit completed and CardConnect returned the
              // signature URL. Surface the handoff. Before sending the
              // recipient off-site, set expectations clearly (issue #35):
              // they are going to CardConnect (a third party) to sign and
              // will return here. Without this, non-technical owners "freak"
              // and call for help, defeating the self-serve flow.
              <>
                <p className="text-brand-text">
                  Your application has been accepted. The final step is your
                  digital signature.
                </p>
                <div className="panel mt-4 px-4 py-3 text-sm text-brand-muted">
                  <p>
                    The button below takes you to{" "}
                    <span className="font-medium text-brand-text">
                      CardConnect
                    </span>
                    , our secure payments partner, to sign. It opens in a new
                    tab, and once you're done, you can close both the
                    CardConnect tab and this tab. Nothing else is needed on this
                    page.
                  </p>
                </div>
                <a
                  href={signatureUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setSignatureLaunched(true)}
                  className="btn-primary mt-4 py-3 text-base"
                >
                  Continue to CardConnect to sign
                </a>
                <p className="mt-3 text-sm text-brand-muted">
                  We've also emailed you the same link in case you'd rather come
                  back to it later.
                </p>
              </>
            )
          ) : saveError ? (
            <div
              aria-live="assertive"
              className="callout-danger px-4 py-3"
            >
              <p className="font-medium">
                There was an error processing your application: {saveError}.
                Refresh and try again.
              </p>
            </div>
          ) : (
            // Auto-submit kicked off; URL not yet available. Show a
            // "checking..." state for a bit and fall back to the
            // email-only copy if the poll times out without a URL.
            <>
              <p className="text-brand-muted">
                Thanks. Your application is being processed. You should receive
                an email with a link to complete your signature soon, and we'll
                show a "Sign now" button here as soon as it's ready. If the
                button does not appear within a minute, please contact your
                Payment Products representative.
              </p>
              <p className="mt-3 text-sm text-brand-muted">
                If you don't see the email within one business day, check your
                spam folder, then contact your Payment Products representative.
                We'll follow up directly if there's any issue with your
                application.
              </p>
            </>
          )
        ) : enrollmentOnly ? (
          <p className="text-brand-muted">
            Thank you. Your {mnspSectionLabel} has been received. Your Payment
            Products representative will be in touch if anything else is needed.
          </p>
        ) : (
          <>
            <p className="text-brand-muted">
              Thanks. Your representative will review the application and an
              email requesting your signature will be sent.
            </p>
            <p className="mt-3 text-sm text-brand-muted">
              If you don't hear back within one business day, please contact
              your Payment Products representative.
            </p>
          </>
        )}
        {autoFillNotice && (
          <p className="mt-4 text-sm text-brand-muted">{autoFillNotice}</p>
        )}
      </Shell>
    );
  }

  const businessName =
    meta?.businessName || form.businessName || "your business";

  // Pricing confirmation dialog (issue #62). Defined once and rendered in
  // BOTH the sign-only (mnspOnly) branch below and the full-intake return, so
  // a bundled application shows it after Submit on either page. It only opens
  // when handleSubmit sets showPricingConfirm, which happens for bundled apps
  // (never MNSP-only enrollment). Rendering it only in the full-intake return
  // previously stalled the sign-only page's Submit (Susan, dev 2026-09-09).
  const pricingConfirmDialog = (
    <ConfirmDialog
      open={showPricingConfirm}
      title="Confirm your application"
      body={
        <PricingConfirmBody
          open={showPricingConfirm}
          volume={form.volumeDetails.averageMonthlyVolume}
          authFeeOverride={meta?.authFeeOverride}
          feeOverrides={meta?.feeOverrides}
          icPlusPricing={meta?.icPlusPricing}
          fleetOverrides={meta?.fleetOverrides}
          cloverSecurityAndTransarmor={meta?.cloverSecurityAndTransarmor}
          annualSecurityBundleFee={meta?.annualSecurityBundleFee}
        />
      }
      confirmLabel="Confirm and submit"
      cancelLabel="Go back"
      busy={submitting}
      onCancel={() => setShowPricingConfirm(false)}
      onConfirm={() => {
        setShowPricingConfirm(false);
        void performSubmit();
      }}
    />
  );

  // MNSP-only mode: admin already submitted the merchant application to
  // CardConnect. Show only the MNSP signing section — no merchant fields,
  // no save bar progress (there's nothing to fill in), no submit button
  // label about "application".
  if (mnspOnly) {
    return (
      <Shell>
        <h1 className="mb-1 text-2xl font-semibold tracking-tight text-white">
          {mnspSectionLabel} for {businessName}
        </h1>
        <p className="mb-6 text-sm leading-6 text-brand-muted">
          {enrollmentOnly
            ? `Please review the terms below, acknowledge each section, and sign to complete your ${mnspSectionLabel}.`
            : `Your merchant application has been submitted. Please review the terms below, acknowledge each section, and sign to complete your ${mnspSectionLabel}.`}
        </p>

        <form
          onSubmit={handleSubmit}
          onBlurCapture={onBlurCapture}
          className="space-y-6"
          noValidate
          autoComplete="off"
        >
          <div id="mnsp-enrollment-section-anchor">
            <MnspEnrollmentSection
              form={mnspForm}
              onChange={setMnspFormAndSave}
              mnspEnabled={mnspRequested}
              rentalEnabled={terminalRentalRequested}
              error={mnspError}
              onlySignature
              uploads={docUploads}
              uploadPreviews={docPreviewUrls}
              onUpload={handleUploadDocument}
              showUploadErrors={showUploadErrors}
            />
          </div>

          {visibleErrors.length > 0 && (
            <div
              ref={errorBannerRef}
              role="alert"
              aria-live="assertive"
              className="callout-danger flex gap-3 px-4 py-3"
            >
              <AlertCircle
                className="mt-0.5 h-4 w-4 shrink-0 text-danger"
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-white">
                  {visibleErrors.length === 1
                    ? "1 issue"
                    : `${visibleErrors.length} issues`}{" "}
                  before submitting
                </p>
                <ul className="mt-1.5 space-y-0.5 text-danger-soft">
                  {visibleErrors.map((e) => (
                    <li key={e.field}>{e.message}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div
            className="sticky bottom-0 -mx-4 mt-6 border-t border-brand-border bg-brand-bg/90 p-4 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.6)] backdrop-blur"
            style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
          >
            <button
              id="intake-submit"
              type="submit"
              disabled={submitting}
              className="btn-primary w-full py-3 text-base"
            >
              {submitting
                ? "Submitting…"
                : `Submit ${mnspSectionLabel}`}
            </button>
            {saveError && (
              <p role="alert" className="mt-2 text-center text-sm text-danger">
                {saveError}
              </p>
            )}
          </div>
        </form>
        {pricingConfirmDialog}
      </Shell>
    );
  }

  // Live submit-gate. The button stays disabled until every required
  // field passes localValidate() and (when the MNSP bundle is present)
  // all four acceptances, the printed name, and the signature are in.
  // These are the exact same conditions handleSubmit enforces on click,
  // so disabling the button never blocks a submit that would otherwise
  // succeed -- it just surfaces the gate earlier. localValidate() is a
  // pure read of form state, safe to call during render.
  const remainingRequired = getIntakeSubmitRemaining({
    form,
    mailingSameAsBusiness,
    withdrawalSameAsDeposit,
    enrollmentOnly,
    isCensored,
    mnspRequested,
    terminalRentalRequested,
    mnspForm,
    hasDriversLicense: !!docUploads.driversLicense,
    hasVoidedCheck: !!docUploads.voidedCheck,
  });
  const canSubmit = remainingRequired === 0;

  return (
    <Shell>
      <a
        href="#intake-submit"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-brand-blue focus:px-3 focus:py-1 focus:text-sm focus:text-white"
      >
        Skip to submit
      </a>

      <SaveBar
        state={saveState}
        error={saveError}
        pct={progressPct}
        lastSavedAt={lastSavedAt}
        canSubmit={canSubmit}
        remaining={remainingRequired}
      />

      {welcomeBackBanner && (
        <div className="callout-info mb-4 flex items-start justify-between gap-3 p-4">
          <div>
            <p className="font-medium">Welcome back.</p>
            <p className="mt-1 text-brand-muted">
              Your progress is saved ({progressPct}% complete), but your
              application has not been submitted yet. Sensitive fields you
              already entered (SSN, Tax ID, account/routing numbers) show the
              last 4 digits as <code>****1234</code>; type fresh values to
              replace them, or leave them as-is.
            </p>
          </div>
          <button
            type="button"
            onClick={dismissWelcomeBanner}
            aria-label="Dismiss welcome banner"
            className="rounded p-1 text-brand-muted hover:bg-brand-border/40 hover:text-brand-text"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      <h1 className="mb-2 text-2xl font-semibold tracking-tight text-white">
        Merchant application for {businessName}
      </h1>
      <p className="mb-6 text-sm leading-6 text-brand-muted">
        Fill in the fields below. Your progress saves automatically — you can
        close this page and come back from the same link any time. Submit at the
        bottom when you're done.
      </p>

      <IntakeLocksContext.Provider value={lockedFields}>
      <form
        onSubmit={handleSubmit}
        onBlurCapture={onBlurCapture}
        noValidate
        autoComplete="off"
      >
        <div className="card space-y-7 p-5 sm:p-7">
          <Section title="Business">
            <Row>
              <TF
                id="businessName"
                label="Legal business name"
                value={form.businessName}
                onChange={(v) => setField("businessName", v)}
                required
                maxLength={200}
                error={fieldError("businessName")}
              />
              <TF
                id="dbaName"
                label="DBA name"
                value={form.dbaName}
                onChange={(v) => setField("dbaName", v)}
                hint="Leave blank to use your legal business name."
                maxLength={200}
              />
              <TF
                id="taxFilingName"
                label="Tax filing name"
                hint="Defaults to legal name if blank."
                value={form.taxFilingName}
                onChange={(v) => setField("taxFilingName", v)}
                format="alphanum"
                maxLength={200}
              />
              <Field
                id="businessStartDate"
                label="Business start date"
                required
                error={fieldError("businessStartDate")}
              >
                <DatePicker
                  id="ife-businessStartDate"
                  value={form.businessStartDate}
                  onChange={(v) => setField("businessStartDate", v)}
                  rules={businessStartDateRules()}
                  error={fieldError("businessStartDate")}
                  required
                  ariaInvalid={!!fieldError("businessStartDate")}
                  ariaDescribedBy={
                    fieldError("businessStartDate")
                      ? "ife-businessStartDate-err"
                      : undefined
                  }
                />
              </Field>
            </Row>
            <Row>
              <Field id="taxFilingMethod" label="Tax filing method" required>
                <select
                  id="ife-taxFilingMethod"
                  className="input"
                  value={form.taxFilingMethod}
                  onChange={(e) => setField("taxFilingMethod", e.target.value)}
                  aria-required="true"
                >
                  <option value="EIN">EIN</option>
                  <option value="SSN">SSN</option>
                </select>
              </Field>
              <Field
                id="taxID"
                label="Tax ID (EIN or SSN)"
                sensitive
                required
                error={fieldError("taxID")}
              >
                <SensitiveField
                  id="ife-taxID"
                  value={form.taxID || ""}
                  onChange={(v) =>
                    setField(
                      "taxID",
                      isCensored("taxID") && v.startsWith("****")
                        ? v
                        : v.replace(/\D/g, "").slice(0, 9),
                    )
                  }
                  placeholder="123456789"
                  inputMode="numeric"
                  maxLength={isCensored("taxID") ? 20 : 9}
                  ariaInvalid={!!fieldError("taxID")}
                  ariaDescribedBy={
                    fieldError("taxID") ? "ife-taxID-err" : undefined
                  }
                  alwaysVisible={isCensored("taxID")}
                />
              </Field>
            </Row>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <TF
                id="averageMonthlyVolume"
                label="Average monthly volume"
                value={form.volumeDetails.averageMonthlyVolume}
                required
                error={fieldError("averageMonthlyVolume")}
                format="integer"
                onChange={(v) =>
                  patchForm((p) => ({
                    ...p,
                    volumeDetails: {
                      ...p.volumeDetails,
                      averageMonthlyVolume: v,
                    },
                  }))
                }
                maxLength={12}
              />
              <TF
                id="highTicketAmount"
                label="Highest ticket amount"
                value={form.volumeDetails.highTicketAmount}
                format="integer"
                onChange={(v) =>
                  patchForm((p) => ({
                    ...p,
                    volumeDetails: {
                      ...p.volumeDetails,
                      highTicketAmount: v,
                    },
                  }))
                }
                maxLength={12}
              />
              <TF
                id="averageTicketAmount"
                label="Average ticket amount"
                value={form.volumeDetails.averageTicketAmount}
                required
                error={fieldError("averageTicketAmount")}
                format="integer"
                onChange={(v) =>
                  patchForm((p) => ({
                    ...p,
                    volumeDetails: {
                      ...p.volumeDetails,
                      averageTicketAmount: v,
                    },
                  }))
                }
                maxLength={12}
              />
            </div>
            {/* Pricing-tier display intentionally NOT rendered here (issue
                #62, Michael 2026-07-13): a live "you qualify for" preview
                next to the volume input invites gaming the number. Pricing
                is shown once, on the confirmation dialog at submit. */}
            <Row>
              <Field
                id="mcc"
                label="Merchant Category (MCC)"
                required
                error={fieldError("mcc")}
              >
                <SearchableSelect
                  id="ife-mcc"
                  value={form.mcc}
                  options={[]}
                  onChange={(v) => {
                    setField("mcc", v);
                    setField("storeType", "");
                  }}
                  ariaInvalid={!!fieldError("mcc")}
                  ariaDescribedBy={fieldError("mcc") ? "ife-mcc-err" : undefined}
                  selectedLabel={
                    form.mcc && MCC_BY_CODE[form.mcc]
                      ? formatMccLabel(MCC_BY_CODE[form.mcc])
                      : undefined
                  }
                  customFilter={(q) =>
                    searchMcc(q).map((m) => ({
                      value: m.code,
                      label: formatMccLabel(m),
                      hint: m.description,
                    }))
                  }
                  renderItem={(opt) => (
                    <div>
                      <div className="font-mono text-xs text-brand-muted">
                        {opt.value}
                      </div>
                      <div>{opt.hint ?? opt.label}</div>
                    </div>
                  )}
                  placeholder="Type to search…"
                  inputMode="text"
                />
                {form.mcc && MCC_BY_CODE[form.mcc] && (
                  <p className="mt-1 text-xs text-brand-muted">
                    Selected: {formatMccLabel(MCC_BY_CODE[form.mcc])}
                  </p>
                )}
              </Field>
              <TF
                id="website"
                label="Website"
                hint="Leave blank if you don't have one."
                value={form.website}
                onChange={(v) => setField("website", v)}
                placeholder="https://"
                inputMode="text"
                error={fieldError("website")}
              />
            </Row>
            {form.mcc && storeTypes.some((s) => s.mccCode === form.mcc) && (
              <Field id="storeType" label="Store type">
                <select
                  id="ife-storeType"
                  className={INTAKE_INPUT}
                  value={form.storeType}
                  onChange={(e) => setField("storeType", e.target.value)}
                >
                  <option value="">Select one…</option>
                  {storeTypes
                    .filter((s) => s.mccCode === form.mcc)
                    .map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.name}
                      </option>
                    ))}
                </select>
              </Field>
            )}
            <TF
              id="businessDescription"
              label="Business description"
              value={form.businessDescription}
              onChange={(v) => setField("businessDescription", v)}
              maxLength={500}
            />
            <Row>
              <TF
                id="contactEmail"
                label="Contact email"
                type="email"
                inputMode="email"
                value={form.contactEmail}
                onChange={(v) => setField("contactEmail", v)}
                maxLength={320}
                required
                error={fieldError("contactEmail")}
              />
              <TF
                id="contactPhone"
                label="Business phone"
                value={form.contactPhone}
                onChange={(v) => setField("contactPhone", v)}
                format="phone"
                required
                error={fieldError("contactPhone")}
              />
            </Row>
          </Section>

          <Section title="Business address">
            <AddressFields
              prefix="businessAddress"
              value={form.businessAddress}
              onChange={(a) => setField("businessAddress", a)}
              errorAt={fieldError}
              showCountry={showCountry}
              onShowCountry={() => setShowCountry(true)}
            />
          </Section>

          <Section title="Mailing address">
            <SameAsToggle
              checked={mailingSameAsBusiness}
              onChange={setMailingSameAsBusiness}
              disabled={mailingLocked}
              label="Mailing address is the same as the business address"
              subtitle="Mailing address"
            >
              <AddressFields
                prefix="mailingAddress"
                value={form.mailingAddress}
                onChange={(a) => setField("mailingAddress", a)}
                errorAt={fieldError}
                showCountry={showCountry}
                onShowCountry={() => setShowCountry(true)}
              />
            </SameAsToggle>
          </Section>

          <Section title="Owner">
            <Row>
              <TF
                id="owner.firstName"
                label="First name"
                value={form.owner.firstName}
                format="name"
                onChange={(v) =>
                  patchForm((p) => ({
                    ...p,
                    owner: { ...p.owner, firstName: v },
                  }))
                }
                required
                error={fieldError("owner.firstName")}
              />
              <TF
                id="owner.lastName"
                label="Last name"
                value={form.owner.lastName}
                format="name"
                onChange={(v) =>
                  patchForm((p) => ({
                    ...p,
                    owner: { ...p.owner, lastName: v },
                  }))
                }
                required
                error={fieldError("owner.lastName")}
              />
            </Row>
            <Row>
              <Field id="owner.ownershipTypeCd" label="Ownership type" required>
                <SelectField
                  id="ife-owner.ownershipTypeCd"
                  value={form.owner.ownershipTypeCd}
                  options={OWNERSHIP_TYPE_OPTIONS}
                  onChange={(v) =>
                    patchForm((p) => ({
                      ...p,
                      owner: { ...p.owner, ownershipTypeCd: v },
                    }))
                  }
                />
              </Field>
              <Field
                id="owner.title"
                label="Owner title"
                required
                error={fieldError("owner.title")}
              >
                <SelectField
                  id="ife-owner.title"
                  value={form.owner.title}
                  options={OWNER_TITLE_OPTIONS}
                  onChange={(v) =>
                    patchForm((p) => ({ ...p, owner: { ...p.owner, title: v } }))
                  }
                  ariaInvalid={!!fieldError("owner.title")}
                  ariaDescribedBy={
                    fieldError("owner.title") ? "ife-owner.title-err" : undefined
                  }
                />
              </Field>
              <TF
                id="owner.ownershipPct"
                label="Ownership %"
                value={form.owner.ownershipPct}
                format="integer"
                onChange={(v) =>
                  patchForm((p) => ({
                    ...p,
                    owner: { ...p.owner, ownershipPct: v },
                  }))
                }
                maxLength={3}
                error={fieldError("owner.ownershipPct")}
              />
            </Row>
            <Row>
              <TF
                id="owner.email"
                label="Owner email"
                type="email"
                inputMode="email"
                value={form.owner.email}
                onChange={(v) =>
                  patchForm((p) => ({ ...p, owner: { ...p.owner, email: v } }))
                }
                required
                error={fieldError("owner.email")}
              />
              <TF
                id="owner.phone"
                label="Owner phone"
                value={form.owner.phone}
                onChange={(v) =>
                  patchForm((p) => ({
                    ...p,
                    owner: { ...p.owner, phone: v },
                  }))
                }
                format="phone"
                required
                error={fieldError("owner.phone")}
              />
            </Row>
            <Row>
              <Field
                id="owner.ssn"
                label="Owner SSN"
                sensitive
                required
                error={fieldError("owner.ssn")}
              >
                <SensitiveField
                  id="ife-owner.ssn"
                  value={form.owner.ssn || ""}
                  onChange={(v) =>
                    patchForm((p) => ({
                      ...p,
                      owner: {
                        ...p.owner,
                        ssn:
                          isCensored("ssn") && v.startsWith("****")
                            ? v
                            : formatSSN(v),
                      },
                    }))
                  }
                  placeholder="123-45-6789"
                  inputMode="numeric"
                  maxLength={11}
                  ariaInvalid={!!fieldError("owner.ssn")}
                  ariaDescribedBy={
                    fieldError("owner.ssn") ? "ife-owner.ssn-err" : undefined
                  }
                  alwaysVisible={isCensored("ssn")}
                />
              </Field>
              <Field
                id="owner.dateOfBirth"
                label="Date of birth"
                required
                error={fieldError("owner.dateOfBirth")}
              >
                <DatePicker
                  id="ife-owner.dateOfBirth"
                  value={form.owner.dateOfBirth}
                  onChange={(v) =>
                    patchForm((p) => ({
                      ...p,
                      owner: { ...p.owner, dateOfBirth: v },
                    }))
                  }
                  rules={ownerDobRules()}
                  error={fieldError("owner.dateOfBirth")}
                  required
                  ariaInvalid={!!fieldError("owner.dateOfBirth")}
                  ariaDescribedBy={
                    fieldError("owner.dateOfBirth")
                      ? "ife-owner.dateOfBirth-err"
                      : undefined
                  }
                />
              </Field>
            </Row>
            <Row>
              <Field
                id="owner.driversLicenseNumber"
                label="Driver's license number"
                sensitive
                required
                error={fieldError("owner.driversLicenseNumber")}
              >
                <SensitiveField
                  id="ife-owner.driversLicenseNumber"
                  value={form.owner.driversLicenseNumber || ""}
                  onChange={(v) =>
                    patchForm((p) => {
                      const prev = p.owner.driversLicenseNumber || "";
                      // Censored DL hydrates as ****1234. On the first edit,
                      // drop the mask so the typed characters fully replace
                      // it. Without this, appending yields ****1234X, which
                      // toWire's unmask() discards as a masked no-op --
                      // silently dropping the recipient's correction. Mirrors
                      // the ssn/taxID preservation contract.
                      let next = v;
                      if (
                        isCensored("driversLicenseNumber") &&
                        prev.startsWith("****") &&
                        v.startsWith("****")
                      ) {
                        next = v.length > prev.length ? v.slice(prev.length) : "";
                      }
                      return {
                        ...p,
                        owner: { ...p.owner, driversLicenseNumber: next },
                      };
                    })
                  }
                  inputMode="text"
                  maxLength={isCensored("driversLicenseNumber") ? 54 : 50}
                  ariaInvalid={!!fieldError("owner.driversLicenseNumber")}
                  ariaDescribedBy={
                    fieldError("owner.driversLicenseNumber")
                      ? "ife-owner.driversLicenseNumber-err"
                      : undefined
                  }
                  alwaysVisible={isCensored("driversLicenseNumber")}
                />
              </Field>
              <Field
                id="owner.driversLicenseStateCd"
                label="DL state"
                required
                error={fieldError("owner.driversLicenseStateCd")}
              >
                <SelectField
                  id="ife-owner.driversLicenseStateCd"
                  value={form.owner.driversLicenseStateCd}
                  options={US_STATE_OPTIONS}
                  onChange={(v) =>
                    patchForm((p) => ({
                      ...p,
                      owner: { ...p.owner, driversLicenseStateCd: v },
                    }))
                  }
                  ariaInvalid={!!fieldError("owner.driversLicenseStateCd")}
                  ariaDescribedBy={
                    fieldError("owner.driversLicenseStateCd")
                      ? "ife-owner.driversLicenseStateCd-err"
                      : undefined
                  }
                />
              </Field>
            </Row>
            <p className="mt-3 mb-1 text-xs uppercase tracking-wide text-brand-muted">
              Owner address
            </p>
            <AddressFields
              prefix="owner.address"
              value={form.owner.address}
              onChange={(a) =>
                patchForm((p) => ({ ...p, owner: { ...p.owner, address: a } }))
              }
              errorAt={fieldError}
              showCountry={showCountry}
              onShowCountry={() => setShowCountry(true)}
            />
          </Section>

          <Section
            title="Banking"
            description="Account and routing numbers are sent to our processor for boarding; we keep only the last 4 digits."
          >
            <p className="form-subsection-title">Deposit account</p>
            <BankAccountFields
              prefix="bankDetail.deposit"
              value={form.bankDetail.deposit}
              onChange={(a) =>
                patchForm((p) => ({
                  ...p,
                  bankDetail: { ...p.bankDetail, deposit: a },
                }))
              }
              errorAt={fieldError}
              censoredFields={censoredFields}
              depositSide
            />
            <SameAsToggle
              checked={withdrawalSameAsDeposit}
              onChange={setWithdrawalSameAsDeposit}
              disabled={withdrawalLocked}
              label="Withdrawal account is the same as the deposit account"
              subtitle="Withdrawal account"
            >
              <BankAccountFields
                prefix="bankDetail.withdrawal"
                value={form.bankDetail.withdrawal}
                onChange={(a) =>
                  patchForm((p) => ({
                    ...p,
                    bankDetail: { ...p.bankDetail, withdrawal: a },
                  }))
                }
                errorAt={fieldError}
                censoredFields={censoredFields}
              />
            </SameAsToggle>
          </Section>

          <Section
            title="Transaction mix"
            description="How card payments come in, as percentages. Must total 100."
            aside={
              <PercentageTotalIndicator
                id="ife-modeOfTransaction-total"
                total={modeTotal}
              />
            }
          >
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <TF
                id="modeOfTransaction.swipedPct"
                label="Swiped %"
                format="integer"
                maxLength={3}
                required
                value={form.modeOfTransaction.swipedPct}
                error={fieldError("modeOfTransaction.swipedPct")}
                onChange={(v) =>
                  patchForm((p) => ({
                    ...p,
                    modeOfTransaction: {
                      ...p.modeOfTransaction,
                      swipedPct: v,
                    },
                  }))
                }
              />
              <TF
                id="modeOfTransaction.keyedPct"
                label="Keyed %"
                format="integer"
                maxLength={3}
                required
                value={form.modeOfTransaction.keyedPct}
                error={fieldError("modeOfTransaction.keyedPct")}
                onChange={(v) =>
                  patchForm((p) => ({
                    ...p,
                    modeOfTransaction: {
                      ...p.modeOfTransaction,
                      keyedPct: v,
                    },
                  }))
                }
              />
              <TF
                id="modeOfTransaction.eCommercePct"
                label="eCommerce %"
                format="integer"
                maxLength={3}
                required
                value={form.modeOfTransaction.eCommercePct}
                error={fieldError("modeOfTransaction.eCommercePct")}
                onChange={(v) =>
                  patchForm((p) => ({
                    ...p,
                    modeOfTransaction: {
                      ...p.modeOfTransaction,
                      eCommercePct: v,
                    },
                  }))
                }
              />
              <TF
                id="modeOfTransaction.mailOrderPct"
                label="Mail/phone %"
                format="integer"
                maxLength={3}
                required
                value={form.modeOfTransaction.mailOrderPct}
                error={fieldError("modeOfTransaction.mailOrderPct")}
                onChange={(v) =>
                  patchForm((p) => ({
                    ...p,
                    modeOfTransaction: {
                      ...p.modeOfTransaction,
                      mailOrderPct: v,
                    },
                  }))
                }
              />
            </div>
            {/* The "must total 100" rule belongs to the group, not to any
                one input, so it gets its own focus target. */}
            <div id="ife-modeOfTransaction" tabIndex={-1}>
              {fieldError("modeOfTransaction") && (
                <FieldErrorText id="ife-modeOfTransaction-err" className="!mt-0">
                  {fieldError("modeOfTransaction")}
                </FieldErrorText>
              )}
            </div>
          </Section>

          {/* Hidden whenever no PDI managed-network order is in play --
              MNSP-enrollment-only by default, or any application the
              operator explicitly opted out (sendPdiOrder="No"). Defaults are
              still submitted on the wire either way (pdiPrefsRef mirror in
              scheduleSave), just not shown as a setup section. */}
          {pdiOrderInPlay && (
            <Section
              title="Equipment & network (PDI)"
              description="Defaults are applied; expand to tell us about your setup."
              aside={
                <button
                  type="button"
                  onClick={() => setPdiOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-brand-slate transition-colors hover:bg-brand-border/40 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-bright/40"
                  aria-expanded={pdiOpen}
                  aria-controls="ife-pdi-section"
                >
                  {pdiOpen ? "Hide" : "Show"}
                  {pdiOpen ? (
                    <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                </button>
              }
            >
              {!pdiOpen && <div id="ife-pdi-section" hidden />}
              {pdiOpen && (
                <div id="ife-pdi-section" className="space-y-5">
                  <div>
                    <label
                      htmlFor="ife-pdi-pos"
                      className="label mb-1.5 block"
                    >
                      POS platform
                    </label>
                    <SelectField
                      id="ife-pdi-pos"
                      value={
                        posOtherMode
                          ? POS_PLATFORM_OTHER
                          : (pdiPrefs.posPlatform ?? "")
                      }
                      options={[
                        ...POS_PLATFORM_OPTIONS,
                        { value: POS_PLATFORM_OTHER, label: "Other (not listed)" },
                      ]}
                      onChange={(v) => {
                        if (v === POS_PLATFORM_OTHER) {
                          // Switch to free-text mode; clear any known value so
                          // the recipient types the actual platform.
                          setPosOtherSelected(true);
                          updatePdi("posPlatform", "");
                        } else {
                          setPosOtherSelected(false);
                          updatePdi("posPlatform", v);
                        }
                      }}
                      selectedLabel={
                        posOtherMode ? "Other (not listed)" : undefined
                      }
                    />
                    {posOtherMode && (
                      <div className="mt-2">
                        <TF
                          id="pdi-pos-other"
                          label="Enter POS platform"
                          value={pdiPrefs.posPlatform ?? ""}
                          onChange={(v) => updatePdi("posPlatform", v)}
                          maxLength={200}
                          placeholder="e.g., a cloud or regional POS not listed above"
                        />
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="ife-pdi-sites"
                        className="label mb-1.5 block"
                      >
                        Site count
                      </label>
                      <select
                        id="ife-pdi-sites"
                        className={INTAKE_INPUT}
                        value={pdiPrefs.singleOrMultiSite ?? "Single"}
                        onChange={(e) =>
                          updatePdi("singleOrMultiSite", e.target.value)
                        }
                      >
                        <option value="Single">Single site</option>
                        <option value="Multi">Multi-site</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label
                        htmlFor="ife-pdi-gas"
                        className="label mb-1.5 block"
                      >
                        Gas pumps?
                      </label>
                      <select
                        id="ife-pdi-gas"
                        className={INTAKE_INPUT}
                        value={pdiPrefs.gasPumpYn ?? "No"}
                        onChange={(e) => updatePdi("gasPumpYn", e.target.value)}
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>
                    <div>
                      <label
                        htmlFor="ife-pdi-broadband"
                        className="label mb-1.5 block"
                      >
                        New broadband?
                      </label>
                      <select
                        id="ife-pdi-broadband"
                        className={INTAKE_INPUT}
                        value={pdiPrefs.broadbandYn ?? "No"}
                        onChange={(e) => updatePdi("broadbandYn", e.target.value)}
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>
                    <div>
                      <label
                        htmlFor="ife-pdi-residential"
                        className="label mb-1.5 block"
                      >
                        Residential?
                      </label>
                      <select
                        id="ife-pdi-residential"
                        className={INTAKE_INPUT}
                        value={pdiPrefs.residentialLocation ?? "No"}
                        onChange={(e) =>
                          updatePdi("residentialLocation", e.target.value)
                        }
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>
                  </div>
                  {pdiPrefs.gasPumpYn === "Yes" && (
                    <div>
                      <label
                        htmlFor="ife-pdi-gas-type"
                        className="label mb-1.5 block"
                      >
                        Gas pump type
                      </label>
                      {/* Dropdown per the 2026-07-13 meeting (issue #66);
                          Greg: "it'll literally be the Gilbarco, Wayne, or
                          Bennett." Other keeps the free-text escape hatch;
                          a stored value outside the known three hydrates
                          into Other mode automatically. */}
                      <select
                        id="ife-pdi-gas-type"
                        className={INTAKE_INPUT}
                        value={
                          gasOtherSelected ||
                          (!!pdiPrefs.gasPumpType &&
                            !GAS_PUMP_TYPES.includes(pdiPrefs.gasPumpType))
                            ? "__other__"
                            : (pdiPrefs.gasPumpType ?? "")
                        }
                        onChange={(e) => {
                          if (e.target.value === "__other__") {
                            setGasOtherSelected(true);
                            updatePdi("gasPumpType", "");
                          } else {
                            setGasOtherSelected(false);
                            updatePdi("gasPumpType", e.target.value);
                          }
                        }}
                      >
                        <option value="">Select pump type</option>
                        {GAS_PUMP_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                        <option value="__other__">Other (not listed)</option>
                      </select>
                      {(gasOtherSelected ||
                        (!!pdiPrefs.gasPumpType &&
                          !GAS_PUMP_TYPES.includes(pdiPrefs.gasPumpType))) && (
                        <div className="mt-2">
                          <TF
                            id="pdi-gas-type-other"
                            label="Enter pump type"
                            value={pdiPrefs.gasPumpType ?? ""}
                            onChange={(v) => updatePdi("gasPumpType", v)}
                            maxLength={100}
                            placeholder="e.g., a pump brand not listed above"
                          />
                        </div>
                      )}
                    </div>
                  )}
                  <TF
                    id="pdi-wifi"
                    label="Managed WiFi / switch interest"
                    value={pdiPrefs.managedWifiInterest ?? ""}
                    onChange={(v) => updatePdi("managedWifiInterest", v)}
                    maxLength={200}
                    placeholder="Leave blank for none"
                  />
                  <div>
                    <label
                      htmlFor="ife-pdi-notes"
                      className="label mb-1.5 block"
                    >
                      Notes for PDI
                    </label>
                    <textarea
                      id="ife-pdi-notes"
                      className="input min-h-[88px] resize-y text-base"
                      value={pdiPrefs.notes ?? ""}
                      onChange={(e) => updatePdi("notes", e.target.value)}
                      maxLength={1000}
                      placeholder="Anything PDI's team should know about this order."
                    />
                  </div>
                </div>
              )}
            </Section>
          )}

          {(mnspRequested || terminalRentalRequested) && (
            <div
              id="mnsp-enrollment-section-anchor"
              className="form-section"
            >
              <MnspEnrollmentSection
                form={mnspForm}
                onChange={setMnspFormAndSave}
                mnspEnabled={mnspRequested}
                rentalEnabled={terminalRentalRequested}
                error={mnspError}
                uploads={docUploads}
                uploadPreviews={docPreviewUrls}
                onUpload={handleUploadDocument}
                showUploadErrors={showUploadErrors}
              />
            </div>
          )}

          {/* CardConnect Only (no MNSP enrollment, no terminal rental): there's
              no MAR section to collect the documents in, so the same upload
              rows get their own section here. Optional on this path -- the
              completion gate only requires them for MNSP / rental. */}
          {!mnspRequested && !terminalRentalRequested && (
            <Section
              title="Documents"
              description="Payment Products keeps these on file. Photos from your phone are fine (JPEG, PNG, or PDF, up to 10MB each)."
            >
              <div className="space-y-3">
                <DocumentUploadRow
                  label="Driver's license"
                  kind="DRIVERS_LICENSE"
                  uploaded={docUploads.driversLicense}
                  existingUrl={docPreviewUrls.driversLicense}
                  onUpload={handleUploadDocument}
                  required={false}
                />
                <DocumentUploadRow
                  label="Voided check"
                  kind="VOIDED_CHECK"
                  uploaded={docUploads.voidedCheck}
                  existingUrl={docPreviewUrls.voidedCheck}
                  onUpload={handleUploadDocument}
                  required={false}
                />
              </div>
            </Section>
          )}

          {visibleErrors.length > 0 && (
            <div
              ref={errorBannerRef}
              role="alert"
              aria-live="assertive"
              className="callout-danger flex gap-3 px-4 py-3"
            >
              <AlertCircle
                className="mt-0.5 h-4 w-4 shrink-0 text-danger"
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-white">
                  {visibleErrors.length}{" "}
                  {visibleErrors.length === 1 ? "field needs" : "fields need"}{" "}
                  attention before submitting
                </p>
                <ul className="mt-1.5 space-y-0.5">
                  {visibleErrors.map((e) => (
                    <li key={e.field}>
                      <button
                        type="button"
                        onClick={() => {
                          const el = document.getElementById(`ife-${e.field}`);
                          if (el) {
                            el.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                            (el as HTMLElement).focus?.();
                          }
                        }}
                        className="text-left text-danger-soft underline-offset-2 hover:text-white hover:underline focus:outline-none focus:underline"
                      >
                        {e.message}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        <div
          className="sticky bottom-0 -mx-4 mt-6 border-t border-brand-border bg-brand-bg/90 p-4 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.6)] backdrop-blur"
          style={{
            paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
          }}
        >
          <button
            id="intake-submit"
            type="submit"
            disabled={submitting || !canSubmit}
            className="btn-primary w-full py-3 text-base"
          >
            {submitting ? "Submitting…" : "Submit application"}
          </button>
          {visibleErrors.length > 0 ? (
            <p className="mt-2 text-center text-xs font-medium text-danger">
              {visibleErrors.length}{" "}
              {visibleErrors.length === 1 ? "issue" : "issues"} above; scroll up
              to fix.
            </p>
          ) : !canSubmit && !submitting ? (
            <p
              className="mt-2 cursor-pointer text-center text-xs text-brand-muted underline-offset-2 hover:text-brand-text hover:underline"
              onClick={() => {
                // Always surface MNSP issues (sets upload highlights +
                // inline error) so the missing uploads/acceptances light
                // up even when merchant-data fields are already complete.
                const mnspMissing = surfaceMnspErrors();
                const errs = beginSubmit();
                if (errs.length > 0) {
                  focusFirstError(errs);
                  return;
                }
                if (mnspMissing) {
                  scrollToMnspSection();
                }
              }}
            >
              {remainingRequired}{" "}
              {remainingRequired === 1
                ? "required field still needs"
                : "required fields still need"}{" "}
              attention before you can submit. Click here for details.
            </p>
          ) : (
            // Ready state: everything is filled, but nothing is submitted
            // until the merchant actually taps the button. Say so explicitly
            // so a saved, complete-looking form isn't mistaken for a
            // submitted one.
            !submitting && (
              <p className="mt-2 text-center text-xs font-medium text-warning">
                Almost done. Your application is not submitted until you tap
                Submit above.
              </p>
            )
          )}
          {saveError && (
            <p role="alert" className="mt-2 text-center text-sm text-danger">
              {saveError}
            </p>
          )}
        </div>
      </form>
      </IntakeLocksContext.Provider>

      {/* Pricing confirmation (issue #62). Wording per Michael: "this is
          your pricing", not "you qualify for". Shared with the sign-only
          branch above via pricingConfirmDialog. */}
      {pricingConfirmDialog}
    </Shell>
  );
}

// ----- small presentational helpers -----------------------------------------

// Body of the pricing confirmation dialog (issue #62). Phrased as "this
// is your pricing" (Michael 2026-07-13), never "you qualify for". Reads
// the tier fresh from the volume so the dialog always matches what will
// be submitted.
function PricingConfirmBody({
  open,
  volume,
  authFeeOverride,
  feeOverrides,
  icPlusPricing,
  fleetOverrides,
  cloverSecurityAndTransarmor,
  annualSecurityBundleFee,
}: {
  open: boolean;
  volume: string;
  authFeeOverride?: number | null;
  feeOverrides?: FeeOverridesInput | null;
  icPlusPricing?: IntakeLoadResponse["icPlusPricing"];
  fleetOverrides?: FleetOverridesInput | null;
  cloverSecurityAndTransarmor?: CloverSecurityAndTransarmorInput | null;
  // Operator override of the Annual security bundle fee; falls back to the
  // $199 tier default. Our records only -- never boarded.
  annualSecurityBundleFee?: number | null;
}) {
  // Carousel page, reset to the first page each time the dialog reopens so a
  // merchant who paged through and hit "Go back" doesn't land mid-carousel
  // on their next confirm attempt.
  const [rawPageIndex, setRawPageIndex] = useState(0);
  useEffect(() => {
    if (open) setRawPageIndex(0);
  }, [open]);
  const tier = selectPricingTier(volume);
  if (!tier) return null;
  // Operator special-merchant override wins over the tier default. Guard for
  // a finite, non-negative number so a bad value can never mislead the
  // merchant; anything else falls back to the tier's standard auth fee.
  const hasOverride =
    typeof authFeeOverride === "number" &&
    Number.isFinite(authFeeOverride) &&
    authFeeOverride >= 0;
  const effectiveAuthFee = hasOverride ? authFeeOverride : tier.fees.authFee;
  // Interchange-plus discount %s (Visa/MC/Discover + Amex). Values are percent
  // numbers (0.15 == 0.15%). Visa/MC/Discover collapse into one row when they
  // match (the default); a per-brand override that differs splits them out.
  // Amex always gets its own row alongside the others in the same "Card
  // percentage pricing" page -- merchants read all their card discounts as
  // one group (unlike ManualFeeAdjustmentSection on the admin side, which
  // edits Amex in its own section). Previously Amex (and a split-out Visa)
  // were pushed into a separate `discountRows` array that nothing ever
  // rendered, so neither ever reached the merchant -- fixed here.
  const fmtPct = (v: number) => `${parseFloat(v.toFixed(4))}%`;
  const cardPctRows: { label: string; value: string }[] = [];
  if (icPlusPricing) {
    const {
      visaDiscountPct: v,
      mastercardDiscountPct: m,
      discoverDiscountPct: d,
      amexDiscountPct: a,
    } = icPlusPricing;
    if (v != null && v === m && m === d) {
      cardPctRows.push({
        label: "Visa / Mastercard / Discover discount",
        value: fmtPct(v),
      });
    } else {
      if (v != null)
        cardPctRows.push({ label: "Visa discount", value: fmtPct(v) });
      if (m != null)
        cardPctRows.push({ label: "Mastercard discount", value: fmtPct(m) });
      if (d != null)
        cardPctRows.push({ label: "Discover discount", value: fmtPct(d) });
    }
    if (a != null)
      cardPctRows.push({ label: "Amex discount", value: fmtPct(a) });
  }
  // An explicitly zeroed card rate is no charge: hide it, the same zero
  // suppression as the fee rows below and the operator's fee schedule.
  for (let i = cardPctRows.length - 1; i >= 0; i--) {
    if (cardPctRows[i].value === "0%") cardPctRows.splice(i, 1);
  }
  // Every field on PricingTier["fees"] (FEE_OVERRIDE_FIELDS, lib/pricingTiers.ts
  // -- same source of truth ManualFeeAdjustmentSection uses on the admin
  // side), split across two merchant-facing pages: "Authorization and
  // transaction fees" (the per-auth/per-txn fields below) and everything
  // else in "Monthly and miscellaneous fees". authFee is the one field
  // excluded from FeeOverridesInput (it keeps its own dedicated
  // authFeeOverride field for backward compatibility), so it's special-cased
  // to the effectiveAuthFee computed above; every other field is resolved
  // the same override-or-tier-default way.
  const AUTH_AND_TRANSACTION_FEE_KEYS: FeeOverrideField["key"][] = [
    "achBatchFee",
    "authFee",
    "voiceAuthFee",
    "addressVerifFee",
    "transactionFee",
  ];
  // The two PCI programs are mutually exclusive (Michael): ANNUAL bills
  // pciAnnualFee + pciNonComplianceFee and has no concierge fee, CONCIERGE
  // bills pciConciergeMonthlyFee instead and has neither of the other two.
  // Showing all three regardless of program would misrepresent what the
  // merchant is actually being billed.
  const pciProgramOverride = feeOverrides?.pciProgramCd;
  const effectivePciProgramCd: PciProgram =
    pciProgramOverride === "CONCIERGE" || pciProgramOverride === "ANNUAL"
      ? pciProgramOverride
      : tier.fees.pciProgramCd;
  const PCI_HIDDEN_KEYS: FeeOverrideField["key"][] =
    effectivePciProgramCd === "ANNUAL"
      ? ["pciConciergeMonthlyFee"]
      : ["pciAnnualFee", "pciNonComplianceFee"];
  // A $0 fee is a real (non-overridden) value, not a missing one -- but it's
  // clutter the merchant doesn't need to see, so currency/currencyString
  // rows that resolve to zero are dropped rather than shown as "$0.00"
  // (Michael). pciProgram/flag rows aren't dollar amounts and are unaffected.
  const effectiveFeeRow = (field: FeeOverrideField) => {
    if (field.key === "authFee") {
      return effectiveAuthFee === 0
        ? null
        : { label: field.label, value: formatFee(effectiveAuthFee) };
    }
    const override = feeOverrides?.[field.key as keyof FeeOverridesInput];
    const tierValue = tier.fees[field.key];
    switch (field.kind) {
      case "currency": {
        const value =
          typeof override === "number" && Number.isFinite(override)
            ? override
            : (tierValue as number);
        return value === 0
          ? null
          : { label: field.label, value: formatFee(value) };
      }
      case "currencyString": {
        const value =
          typeof override === "string" && override.trim() !== ""
            ? override
            : (tierValue as string);
        return Number(value) === 0
          ? null
          : { label: field.label, value: formatFee(value) };
      }
      case "pciProgram": {
        const value =
          override === "CONCIERGE" || override === "ANNUAL"
            ? override
            : (tierValue as string);
        return { label: field.label, value };
      }
      case "flag": {
        const value =
          typeof override === "boolean" ? override : (tierValue as boolean);
        return { label: field.label, value: value ? "On" : "Off" };
      }
    }
  };
  const isFeeRow = (
    row: ReturnType<typeof effectiveFeeRow>,
  ): row is { label: string; value: string } => row !== null;
  const authTransactionFeeRows = FEE_OVERRIDE_FIELDS.filter((f) =>
    AUTH_AND_TRANSACTION_FEE_KEYS.includes(f.key),
  )
    .map(effectiveFeeRow)
    .filter(isFeeRow);
  // cloverPlatformFee/cloverSecurityFee live in the fees object like every
  // other fee, but CLOVER_FEE_OVERRIDE_KEYS (lib/pricingTiers.ts) is the
  // shared source of truth for grouping them with Clover Security +
  // TransArmor instead -- same split ManualFeeAdjustmentSection uses on the
  // admin side.
  const cloverFeeRows = FEE_OVERRIDE_FIELDS.filter((f) =>
    CLOVER_FEE_OVERRIDE_KEYS.includes(
      f.key as (typeof CLOVER_FEE_OVERRIDE_KEYS)[number],
    ),
  )
    .map(effectiveFeeRow)
    .filter(isFeeRow);
  // Dues & assessments / interchange-cost pass-through are operator-facing
  // billing toggles, and the PCI program code itself is implied by which of
  // pciAnnualFee/pciConciergeMonthlyFee is shown above (Michael) -- none of
  // it is something a merchant needs to see on their pricing summary.
  // Excluded here only, still shown/editable in ManualFeeAdjustmentSection
  // on the admin side.
  const miscFeeRows = FEE_OVERRIDE_FIELDS.filter(
    (f) =>
      !AUTH_AND_TRANSACTION_FEE_KEYS.includes(f.key) &&
      !CLOVER_FEE_OVERRIDE_KEYS.includes(
        f.key as (typeof CLOVER_FEE_OVERRIDE_KEYS)[number],
      ) &&
      f.kind !== "flag" &&
      f.kind !== "pciProgram" &&
      !PCI_HIDDEN_KEYS.includes(f.key),
  )
    .map(effectiveFeeRow)
    .filter(isFeeRow);
  // Annual security bundle fee -- our records only (CardConnect has no API
  // field for it), but the paper MPA/CoPilot shows it to the merchant, so the
  // summary does too. The operator override when set, else the $199 tier
  // default; sits with the other yearly/monthly charges. A waived ($0) bundle
  // is dropped, same $0-fee suppression as the rows above.
  const effectiveAsbf = effectiveAnnualSecurityBundleFee(
    tier.annualSecurityBundleFee,
    annualSecurityBundleFee ?? "",
  );
  if (effectiveAsbf > 0)
    miscFeeRows.push({
      label: "Annual security bundle",
      value: formatFee(effectiveAsbf),
    });
  // Voyager / WEX / PIN debit / EBT, same FLEET_OVERRIDE_FIELDS + key-group
  // split (VOYAGER_KEYS/WEX_KEYS/PIN_DEBIT_KEYS/EBT_KEYS, lib/pricingTiers.ts) that
  // FleetPricingAdjustmentSection uses on the admin side, applied here
  // read-only for the merchant (pricingSummarySeparation): the paper
  // MPA/CoPilot shows fleet pricing to the merchant, so this does too. Fleet
  // pricing is display-only -- it's never layered into the CardConnect
  // boarding payload -- but that's a submit-path concern, unrelated to
  // whether it's shown here. A field with no override AND no tier default
  // (pinDebitTransactionFee/ebtFnsNumber/ebtTransactionFee are genuinely
  // unset by default, see FLEET_PRICING_DEFAULTS) is skipped rather than
  // rendered as a blank/misleading row.
  const resolvedTier = tier;
  const effectiveFleetRow = (field: (typeof FLEET_OVERRIDE_FIELDS)[number]) => {
    const override = fleetOverrides?.[field.key as keyof FleetOverridesInput];
    const value =
      typeof override === "number" && Number.isFinite(override)
        ? override
        : resolvedTier.fleetPricing[field.key];
    if (value == null) return null;
    // Same zero suppression as effectiveFeeRow above, for dollar fees AND
    // discount %s: an explicitly zeroed percent is no charge, and the
    // operator's full fee schedule (PricingTierPreview) hides it too, so the
    // two never disagree. The EBT FNS # is an identifier, not a fee, and is
    // unaffected.
    if (field.kind !== "number" && Number(value) === 0) return null;
    return {
      label: field.label,
      value:
        field.kind === "percent"
          ? formatPercent(value)
          : field.kind === "number"
            ? field.key === "ebtFnsNumber"
              ? formatFnsNumber(value)
              : String(value)
            : formatFee(value),
    };
  };
  const fleetGroupRows = (
    keys: (typeof FLEET_OVERRIDE_FIELDS)[number]["key"][],
  ) =>
    FLEET_OVERRIDE_FIELDS.filter((f) => keys.includes(f.key))
      .map(effectiveFleetRow)
      .filter((row): row is { label: string; value: string } => row !== null);
  const voyagerRows = fleetGroupRows(VOYAGER_KEYS);
  const wexRows = fleetGroupRows(WEX_KEYS);
  const pinDebitRows = fleetGroupRows(PIN_DEBIT_KEYS);
  const ebtRows = fleetGroupRows(EBT_KEYS);
  // Clover Security + TransArmor (CLOVER_SECURITY_AND_TRANSARMOR_FIELDS,
  // lib/pricingTiers.ts) -- a THIRD parallel override object, alongside
  // fees/fleetPricing. Unlike fleet pricing, this DOES reach the CardConnect
  // boarding payload, but that's a submit-path concern; the paper
  // MPA/CoPilot shows it to the merchant too, so it's surfaced here as its
  // own page. Every product here defaults fully OFF (see
  // CLOVER_SECURITY_AND_TRANSARMOR_DEFAULTS), so only the dollar-amount
  // fields are shown -- the enabling flags/codes (data-protection flag,
  // token type, bundle code) are configuration, not something a merchant
  // needs to see; the $0-suppression already used for fees/fleet rows means
  // a disabled product's $0 fee is silently omitted, and an enabled one
  // with a real override shows through normally.
  const cloverSecurityAndTransarmorRows = CLOVER_SECURITY_AND_TRANSARMOR_FIELDS.filter(
    (f) => f.kind === "currency",
  )
    .map((field) => {
      const override =
        cloverSecurityAndTransarmor?.[
          field.key as keyof CloverSecurityAndTransarmorInput
        ];
      const tierValue = tier.cloverSecurityAndTransarmor[field.key];
      const value =
        typeof override === "number" && Number.isFinite(override)
          ? override
          : (tierValue as number | null);
      if (value == null || value === 0) return null;
      return { label: field.label, value: formatFee(value) };
    })
    .filter((row): row is { label: string; value: string } => row !== null);
  // Clover's own platform/security fees (above, pulled out of the fees
  // object) share a page with Clover Security + TransArmor rather than
  // sitting in "Monthly and miscellaneous fees" -- same grouping the admin
  // side uses.
  const cloverPageRows = [...cloverFeeRows, ...cloverSecurityAndTransarmorRows];
  const blocks: { title: string; rows: { label: string; value: string }[] }[] =
    [
      ...(cardPctRows.length
        ? [{ title: "Card percentage pricing", rows: cardPctRows }]
        : []),
      ...(voyagerRows.length
        ? [{ title: "Voyager", rows: voyagerRows }]
        : []),
      ...(wexRows.length ? [{ title: "WEX", rows: wexRows }] : []),
      ...(pinDebitRows.length
        ? [{ title: "PIN debit", rows: pinDebitRows }]
        : []),
      ...(ebtRows.length ? [{ title: "EBT", rows: ebtRows }] : []),
      ...(cloverPageRows.length
        ? [
            {
              title: "Clover Security & TransArmor",
              rows: cloverPageRows,
            },
          ]
        : []),
      ...(authTransactionFeeRows.length
        ? [
            {
              title: "Authorization and transaction fees",
              rows: authTransactionFeeRows,
            },
          ]
        : []),
      // Always last (Michael, pricingSummarySeparation): every remaining
      // field on PricingTier["fees"] belongs together in one page, shown
      // after all the volume-independent fleet/card-percentage pages.
      ...(miscFeeRows.length
        ? [{ title: "Monthly and miscellaneous fees", rows: miscFeeRows }]
        : []),
    ];
  // Every block above is now conditional on having at least one row (the
  // $0-fee suppression can empty a fee section entirely), so guard against
  // an all-zero tier leaving nothing to page through.
  if (blocks.length === 0) return null;
  const pageIndex = Math.min(rawPageIndex, blocks.length - 1);
  const activePage = blocks[pageIndex];
  return (
    <div className="text-sm">
      <p>
        Based on what you entered, this is your pricing with Payment Products:
      </p>
      <div className="mt-3">
        <div key={activePage.title}>
          <p className="text-xs font-medium text-brand-text">
            {activePage.title}
          </p>
          <dl className="mt-1 space-y-1">
            {activePage.rows.map((row) => (
              <div key={row.label} className="flex justify-between gap-4">
                <dt className="text-brand-muted">{row.label}</dt>
                <dd className="font-mono">{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            aria-label="Previous section"
            disabled={pageIndex === 0}
            onClick={() => setRawPageIndex((i) => Math.max(0, i - 1))}
            className="rounded-full p-1 text-brand-muted transition-colors hover:text-brand-text disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1.5">
            {blocks.map((block, i) => (
              <button
                key={block.title}
                type="button"
                aria-label={`Go to ${block.title}`}
                aria-current={i === pageIndex}
                onClick={() => setRawPageIndex(i)}
                className={`h-1.5 w-1.5 rounded-full transition-colors ${
                  i === pageIndex ? "bg-brand-blue" : "bg-brand-border"
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            aria-label="Next section"
            disabled={pageIndex === blocks.length - 1}
            onClick={() =>
              setRawPageIndex((i) => Math.min(blocks.length - 1, i + 1))
            }
            className="rounded-full p-1 text-brand-muted transition-colors hover:text-brand-text disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1 text-center text-[11px] text-brand-muted">
          {pageIndex + 1} of {blocks.length}
        </p>
      </div>
      <p className="mt-3 text-xs text-brand-muted">
        Pricing is based on your average monthly processing volume.
        {tier.isPlaceholder ? " (Preview pricing; final values pending.)" : ""}
      </p>
    </div>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="app-bg min-h-screen text-brand-text">
      <div className="mx-auto max-w-5xl px-4 py-6">
        <p className="mb-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-brand-slate/80">
          <span
            className="inline-block h-2 w-2 rounded-full bg-brand-blue-bright"
            aria-hidden="true"
          />
          Payment Products
        </p>
        {children}
      </div>
    </div>
  );
}

function SaveBar({
  state,
  error,
  pct,
  lastSavedAt,
  canSubmit,
  remaining,
}: {
  state: SaveState;
  error: string | null;
  pct: number;
  lastSavedAt: Date | null;
  // Whether the form is fully ready to submit, and how many required items
  // remain otherwise. This bar only renders BEFORE submission (the submitted
  // success screen replaces the whole page), so it always represents a
  // not-yet-submitted row -- see the "Not submitted yet" line below.
  canSubmit: boolean;
  remaining: number;
}) {
  const label = useMemo(() => {
    if (state === "saving") return "Saving…";
    if (state === "saved" && lastSavedAt) {
      const hh = lastSavedAt.getHours().toString().padStart(2, "0");
      const mm = lastSavedAt.getMinutes().toString().padStart(2, "0");
      return `Saved at ${hh}:${mm}`;
    }
    if (state === "saved") return "Saved";
    if (state === "error") return "Save failed";
    return "Ready";
  }, [state, lastSavedAt]);
  const color =
    state === "error"
      ? "text-danger"
      : state === "saving"
        ? "text-brand-muted"
        : "text-success";
  // "Saved" is about the autosave, not the submission -- on its own it reads
  // like "done," which is how a merchant can sign, see a saved/high-progress
  // bar, and leave without submitting. Always show an explicit not-submitted
  // status here so that never reads as complete.
  const submitStatus = canSubmit
    ? "Not submitted yet. Ready to submit at the bottom."
    : `Not submitted yet. ${remaining} ${
        remaining === 1 ? "item" : "items"
      } left.`;
  return (
    <div
      role="status"
      aria-live="polite"
      className="card sticky top-3 z-20 mb-4 flex items-center justify-between gap-3 bg-brand-surface/90 p-3 backdrop-blur"
    >
      <div>
        <p className={`text-xs font-medium ${color}`}>{label}</p>
        <p className="text-xs font-semibold text-warning">{submitStatus}</p>
        {error && <p className="text-xs text-danger/80">{error}</p>}
      </div>
      <div className="flex items-center gap-2">
        <div className="h-2 w-24 overflow-hidden rounded-full bg-brand-bg-deep shadow-well">
          <div
            className="h-full rounded-full bg-gradient-to-r from-brand-blue to-brand-blue-bright transition-all"
            style={{ width: `${Math.max(2, pct)}%` }}
          />
        </div>
        <span className="text-xs tabular-nums text-brand-muted">{pct}%</span>
      </div>
    </div>
  );
}

function Row({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
  );
}

/** Merchant-facing "sent to processor, not stored" marker. Inline (not a
 *  tooltip) because this form is filled on phones where hover help is easy
 *  to miss, and the reassurance matters for trust. */
function SensitiveMark() {
  return (
    <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-brand-border px-1.5 py-px align-middle text-[10px] font-medium normal-case tracking-normal text-brand-muted">
      <Lock className="h-2.5 w-2.5" aria-hidden="true" />
      not stored
    </span>
  );
}

/** Mobile-first input: same look as `.input`, taller tap target. */
const INTAKE_INPUT = "input h-12 text-base";

// Field wrapper handles the label + (required indicator) + (sensitive
// badge) + (inline error). Wrap arbitrary inputs (SelectField, SearchableSelect,
// DatePicker, SensitiveField, native <select>) — TF wraps a plain
// <input> for the common case.
function Field({
  id,
  label,
  required,
  sensitive,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  sensitive?: boolean;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  const errId = `ife-${id}-err`;
  const hintId = `ife-${id}-hint`;
  const locked = useIntakeLock(id);
  const shownHint = locked ? LOCKED_HINT : hint;
  return (
    <div className="min-w-0">
      <label htmlFor={`ife-${id}`} className="label mb-1.5 block">
        {label}
        {required && !locked && (
          <span className="required-marker" aria-hidden="true">
            *
          </span>
        )}
        {sensitive && <SensitiveMark />}
        {locked && <LockMark />}
      </label>
      {locked ? (
        // A disabled fieldset disables every control inside it (select,
        // date picker, masked input), whatever the child component is.
        <fieldset disabled className="contents">
          {children}
        </fieldset>
      ) : (
        children
      )}
      {shownHint && !error && (
        <p id={hintId} className="hint">
          {shownHint}
        </p>
      )}
      {error && <FieldErrorText id={errId}>{error}</FieldErrorText>}
    </div>
  );
}

interface TFProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  inputMode?: "text" | "email" | "tel" | "numeric" | "decimal";
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
  autoComplete?: string;
  sensitive?: boolean;
  hint?: string;
  error?: string;
  pattern?: string;
  format?: FieldFormat;
}

function TF(props: TFProps) {
  const autoId = useId();
  const inputId = `ife-${props.id || autoId}`;
  const errId = `ife-${props.id}-err`;
  // Resolve the length cap and placeholder from the format the same way
  // components/application/TF does. Without this, format="country" lost its
  // 2-character cap here and a recipient could type "USA", which reaches
  // AppSync and fails CardConnect boarding.
  const defaults: {
    maxLength?: number;
    placeholder?: string;
  } = props.format ? FIELD_FORMAT_DEFAULTS[props.format] : {};
  const locked = useIntakeLock(props.id);
  const hint = locked ? LOCKED_HINT : props.hint;
  return (
    <div className="min-w-0">
      <label htmlFor={inputId} className="label mb-1.5 block">
        {props.label}
        {props.required && !locked && (
          <span className="required-marker" aria-hidden="true">
            *
          </span>
        )}
        {props.sensitive && <SensitiveMark />}
        {locked && <LockMark />}
      </label>
      <input
        id={inputId}
        type={props.type ?? "text"}
        inputMode={props.inputMode}
        value={props.value}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          props.onChange(
            props.format
              ? applyFieldFormat(props.format, e.target.value)
              : e.target.value,
          )
        }
        maxLength={props.maxLength ?? defaults.maxLength}
        placeholder={props.placeholder ?? defaults.placeholder}
        aria-required={props.required || undefined}
        autoComplete={props.autoComplete ?? "off"}
        data-1p-ignore="true"
        data-lpignore="true"
        aria-invalid={!!props.error || undefined}
        aria-describedby={props.error ? errId : undefined}
        readOnly={locked}
        aria-readonly={locked || undefined}
        className={
          locked ? `${INTAKE_INPUT} cursor-not-allowed opacity-70` : INTAKE_INPUT
        }
      />
      {hint && !props.error && <p className="hint">{hint}</p>}
      {props.error && <FieldErrorText id={errId}>{props.error}</FieldErrorText>}
    </div>
  );
}

function AddressFields({
  prefix,
  value,
  onChange,
  errorAt,
  showCountry,
  onShowCountry,
}: {
  prefix: string;
  value: AddressForm;
  onChange: (a: AddressForm) => void;
  errorAt: (field: string) => string | undefined;
  showCountry: boolean;
  onShowCountry: () => void;
}) {
  function set<K extends keyof AddressForm>(k: K, v: AddressForm[K]) {
    onChange({ ...value, [k]: v });
  }
  return (
    <>
      <TF
        id={`${prefix}.address1`}
        label="Street address"
        value={value.address1}
        onChange={(v) => set("address1", v)}
        format="address"
        maxLength={200}
        required
        error={errorAt(`${prefix}.address1`)}
      />
      <TF
        id={`${prefix}.address2`}
        label="Apt / suite (optional)"
        value={value.address2}
        onChange={(v) => set("address2", v)}
        format="address"
        maxLength={200}
      />
      <Row>
        <TF
          id={`${prefix}.city`}
          label="City"
          value={value.city}
          onChange={(v) => set("city", v)}
          format="address"
          maxLength={300}
          required
          error={errorAt(`${prefix}.city`)}
        />
        <Field
          id={`${prefix}.stateCd`}
          label="State"
          required
          error={errorAt(`${prefix}.stateCd`)}
        >
          <SelectField
            id={`ife-${prefix}.stateCd`}
            value={value.stateCd}
            options={US_STATE_OPTIONS}
            onChange={(v) => set("stateCd", v)}
            ariaInvalid={!!errorAt(`${prefix}.stateCd`)}
            ariaDescribedBy={
              errorAt(`${prefix}.stateCd`)
                ? `ife-${prefix}.stateCd-err`
                : undefined
            }
          />
        </Field>
      </Row>
      <Row>
        <TF
          id={`${prefix}.zip`}
          label="ZIP"
          value={value.zip}
          onChange={(v) => set("zip", v)}
          format="zip"
          required
          error={errorAt(`${prefix}.zip`)}
        />
        {showCountry ? (
          <TF
            id={`${prefix}.countryCd`}
            label="Country"
            value={value.countryCd}
            onChange={(v) => set("countryCd", v)}
            format="country"
            error={errorAt(`${prefix}.countryCd`)}
          />
        ) : (
          <div className="flex items-end pb-1">
            <button
              type="button"
              onClick={onShowCountry}
              className="text-xs text-brand-muted underline-offset-2 hover:text-brand-text hover:underline"
            >
              Outside the US?
            </button>
          </div>
        )}
      </Row>
    </>
  );
}

function BankAccountFields({
  prefix,
  value,
  onChange,
  errorAt,
  censoredFields,
  depositSide = false,
}: {
  prefix: string;
  value: BankAccountForm;
  onChange: (a: BankAccountForm) => void;
  errorAt: (field: string) => string | undefined;
  censoredFields: string[];
  depositSide?: boolean;
}) {
  function set<K extends keyof BankAccountForm>(k: K, v: BankAccountForm[K]) {
    onChange({ ...value, [k]: v });
  }
  const side: BankSide = depositSide ? "deposit" : "withdrawal";
  const acctCensored = censoredFields.includes(
    bankCensorKey(side, "accountNumber"),
  );
  const routCensored = censoredFields.includes(
    bankCensorKey(side, "routingNumber"),
  );
  return (
    <>
      <Row>
        <Field
          id={`${prefix}.accountNumber`}
          label="Account number"
          sensitive
          required
          error={errorAt(`${prefix}.accountNumber`)}
        >
          <SensitiveField
            id={`ife-${prefix}.accountNumber`}
            value={value.accountNumber || ""}
            onChange={(v) =>
              set(
                "accountNumber",
                acctCensored && v.startsWith("****")
                  ? v
                  : v.replace(/\D/g, "").slice(0, 17),
              )
            }
            inputMode="numeric"
            maxLength={17}
            ariaInvalid={!!errorAt(`${prefix}.accountNumber`)}
            ariaDescribedBy={
              errorAt(`${prefix}.accountNumber`)
                ? `ife-${prefix}.accountNumber-err`
                : undefined
            }
            alwaysVisible={acctCensored}
          />
        </Field>
        <Field
          id={`${prefix}.confirmAccountNumber`}
          label="Confirm account number"
          sensitive
          required
          error={errorAt(`${prefix}.confirmAccountNumber`)}
        >
          <SensitiveField
            id={`ife-${prefix}.confirmAccountNumber`}
            value={value.confirmAccountNumber || ""}
            onChange={(v) =>
              set(
                "confirmAccountNumber",
                acctCensored && v.startsWith("****")
                  ? v
                  : v.replace(/\D/g, "").slice(0, 17),
              )
            }
            inputMode="numeric"
            maxLength={17}
            placeholder="Re-enter to confirm"
            ariaInvalid={!!errorAt(`${prefix}.confirmAccountNumber`)}
            ariaDescribedBy={
              errorAt(`${prefix}.confirmAccountNumber`)
                ? `ife-${prefix}.confirmAccountNumber-err`
                : undefined
            }
            alwaysVisible={acctCensored}
          />
        </Field>
      </Row>
      <Row>
        <Field
          id={`${prefix}.routingNumber`}
          label="Routing number"
          sensitive
          required
          error={errorAt(`${prefix}.routingNumber`)}
        >
          <SensitiveField
            id={`ife-${prefix}.routingNumber`}
            value={value.routingNumber || ""}
            onChange={(v) =>
              set(
                "routingNumber",
                routCensored && v.startsWith("****")
                  ? v
                  : v.replace(/\D/g, "").slice(0, 9),
              )
            }
            inputMode="numeric"
            maxLength={9}
            ariaInvalid={!!errorAt(`${prefix}.routingNumber`)}
            ariaDescribedBy={
              errorAt(`${prefix}.routingNumber`)
                ? `ife-${prefix}.routingNumber-err`
                : undefined
            }
            alwaysVisible={routCensored}
          />
        </Field>
      </Row>
      <Row>
        <Field
          id={`${prefix}.accountType`}
          label="Account type"
          required
          error={errorAt(`${prefix}.accountType`)}
        >
          <SelectField
            id={`ife-${prefix}.accountType`}
            value={value.accountType}
            options={ACCOUNT_TYPE_OPTIONS}
            onChange={(v) => set("accountType", v)}
            ariaInvalid={!!errorAt(`${prefix}.accountType`)}
            ariaDescribedBy={
              errorAt(`${prefix}.accountType`)
                ? `ife-${prefix}.accountType-err`
                : undefined
            }
          />
        </Field>
        <TF
          id={`${prefix}.bankName`}
          label="Bank name"
          value={value.bankName}
          onChange={(v) => set("bankName", v)}
          maxLength={200}
          required
          error={errorAt(`${prefix}.bankName`)}
        />
      </Row>
    </>
  );
}
